import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import { mapLanguage, mapLevel } from '../utils/languageUtils';
import { getUserFirstName, getUserInitials } from '../utils/userUtils';
import { readEpResponse } from '../utils/epCrypto';
import { startSTT, stopSTT } from '../sttClient';
import { speakWithAvatar, stopAvatarSpeech, getCurrentAudio } from '../ttsPlayer';
import './ExamPrep.css';
import './BusinessConversation.css';
import './BrainWarmup.css';

// STT language codes per app language
const STT_LANG = { en: 'en-US', fr: 'fr-FR', de: 'de-DE', es: 'es-ES', it: 'it-IT', ja: 'ja-JP' };

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
const languageNames = { en: 'English', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian', ja: 'Japanese' };

// Build a feedback object from an attempt's STORED scores, used to backfill OLD
// attempts that have no saved agent feedback (the original exam session is gone, so
// the agent can't regenerate). Produces the same shape the result view expects:
// { headline, summary, keep_going_message, highlights: { section_title, items[] },
//   overall, section_scores }. Deterministic and based on the real stored scores.
function buildFeedbackFromScores(at) {
  const langName = languageNames[at.language_code] || at.language_code || '';
  const level = at.level || '';
  const perSkillMax = Number(at.max_score ? at.max_score / 4 : 25) || 25;
  const skills = [
    { key: 'reading', label: 'Reading', score: Number(at.reading_score) || 0 },
    { key: 'listening', label: 'Listening', score: Number(at.listening_score) || 0 },
    { key: 'writing', label: 'Writing', score: Number(at.writing_score) || 0 },
    { key: 'speaking', label: 'Speaking', score: Number(at.speaking_score) || 0 },
  ];
  // Strongest skill (ties -> first in exam order) for the summary line.
  const strongest = skills.reduce((best, s) => (s.score > best.score ? s : best), skills[0]);
  const pct = (s) => (perSkillMax > 0 ? Math.round((s.score / perSkillMax) * 100) : 0);

  const headline = `Here's your ${level ? level + ' ' : ''}${langName} exam summary!`;
  const summary = `You completed your ${langName} mock test. ${strongest.label} was your strongest area at ${pct(strongest)}%. Keep practising across all skills to build steady progress.`;

  // One progress highlight per skill, worded by its score band.
  const bandMessage = (label, p) => {
    if (p >= 75) return `Strong performance in ${label.toLowerCase()}.`;
    if (p >= 50) return `Developing well in ${label.toLowerCase()} — keep it up.`;
    if (p > 0) return `Room to grow in ${label.toLowerCase()}; focused practice will help.`;
    return `Not attempted / no score in ${label.toLowerCase()} this time.`;
  };
  const items = skills.map(s => ({
    section: s.key,
    title: `${s.label}`,
    message: bandMessage(s.label, pct(s)),
    type: pct(s) >= 50 ? 'positive' : 'improve',
  }));

  return {
    headline,
    summary,
    keep_going_message: 'Keep practising regularly and your skills will keep improving with each session!',
    highlights: { section_title: 'Your progress', items },
    overall: { score: Number(at.score) || 0, out_of: Number(at.max_score) || 100 },
    section_scores: skills.map(s => ({ section: s.label, marks: s.score, max_marks: perSkillMax })),
  };
}
// Localized singular word used in the top counter label ("Fragen N of M", etc.)
// across all sections. The bottom nav near the question boxes stays English.
const QUESTION_WORD = { en: 'Question', de: 'Fragen', fr: 'Question', es: 'Pregunta', it: 'Domanda', ja: '質問' };
const langCodeToName = { en: 'English', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian', ja: 'Japanese' };

// Localized UI strings for the speaking recording controls, keyed by exam language.
// Functions receive params (n = count/minutes) so counts/durations stay dynamic.
// English is the fallback for any missing language.
const SPEAK_STRINGS = {
  en: {
    recordingComplete: 'Your recording is complete. You can listen to it before you continue.',
    selectTopics: (n) => `Select ${n} topics to start recording.`,
    selectOneTopic: 'Select a topic to start recording.',
    prepRunning: 'Preparation time is running — the exam timer is paused. Click "Start Recording" when you\'re ready.',
    tapToRecord: (m) => `Tap to record. You have ${m} minute${m > 1 ? 's' : ''} to respond.`,
    recordingInProgress: 'Recording in progress...',
    selectedCount: (c, n) => `Selected: ${c} / ${n}`,
  },
  de: {
    recordingComplete: 'Ihre Aufnahme ist abgeschlossen. Sie können sie anhören, bevor Sie fortfahren.',
    selectTopics: (n) => `Wählen Sie ${n} Themen aus, um mit der Aufnahme zu beginnen.`,
    selectOneTopic: 'Wählen Sie ein Thema aus, um mit der Aufnahme zu beginnen.',
    prepRunning: 'Vorbereitungszeit läuft — der Prüfungs-Timer ist pausiert. Klicken Sie auf „Start Recording", wenn Sie bereit sind.',
    tapToRecord: (m) => `Tippen Sie zum Aufnehmen. Sie haben ${m} Minute${m > 1 ? 'n' : ''} Zeit für Ihre Antwort.`,
    recordingInProgress: 'Aufnahme läuft...',
    selectedCount: (c, n) => `Ausgewählt: ${c} / ${n}`,
  },
  fr: {
    recordingComplete: 'Votre enregistrement est terminé. Vous pouvez l\'écouter avant de continuer.',
    selectTopics: (n) => `Sélectionnez ${n} sujets pour commencer l'enregistrement.`,
    selectOneTopic: 'Sélectionnez un sujet pour commencer l\'enregistrement.',
    prepRunning: 'Le temps de préparation est en cours — le chronomètre de l\'examen est en pause. Cliquez sur « Start Recording » lorsque vous êtes prêt.',
    tapToRecord: (m) => `Appuyez pour enregistrer. Vous avez ${m} minute${m > 1 ? 's' : ''} pour répondre.`,
    recordingInProgress: 'Enregistrement en cours...',
    selectedCount: (c, n) => `Sélectionné : ${c} / ${n}`,
  },
  es: {
    recordingComplete: 'Tu grabación está completa. Puedes escucharla antes de continuar.',
    selectTopics: (n) => `Selecciona ${n} temas para empezar a grabar.`,
    selectOneTopic: 'Selecciona un tema para empezar a grabar.',
    prepRunning: 'El tiempo de preparación está en marcha — el temporizador del examen está en pausa. Haz clic en «Start Recording» cuando estés listo.',
    tapToRecord: (m) => `Toca para grabar. Tienes ${m} minuto${m > 1 ? 's' : ''} para responder.`,
    recordingInProgress: 'Grabación en curso...',
    selectedCount: (c, n) => `Seleccionado: ${c} / ${n}`,
  },
  it: {
    recordingComplete: 'La tua registrazione è completa. Puoi ascoltarla prima di continuare.',
    selectTopics: (n) => `Seleziona ${n} argomenti per iniziare la registrazione.`,
    selectOneTopic: 'Seleziona un argomento per iniziare la registrazione.',
    prepRunning: 'Il tempo di preparazione è in corso — il timer dell\'esame è in pausa. Clicca su «Start Recording» quando sei pronto.',
    tapToRecord: (m) => `Tocca per registrare. Hai ${m} minuto${m > 1 ? 'i' : ''} per rispondere.`,
    recordingInProgress: 'Registrazione in corso...',
    selectedCount: (c, n) => `Selezionati: ${c} / ${n}`,
  },
  ja: {
    recordingComplete: '録音が完了しました。続行する前に確認できます。',
    selectTopics: (n) => `録音を開始するには${n}つのトピックを選択してください。`,
    selectOneTopic: '録音を開始するにはトピックを選択してください。',
    prepRunning: '準備時間が進行中です。試験タイマーは一時停止しています。準備ができたら「Start Recording」をクリックしてください。',
    tapToRecord: (m) => `タップして録音します。回答時間は${m}分です。`,
    recordingInProgress: '録音中...',
    selectedCount: (c, n) => `選択済み: ${c} / ${n}`,
  },
};

// Section order and display metadata — exam sequence: Reading -> Listening -> Writing -> Speaking
const SECTION_ORDER = ['reading', 'listening', 'writing', 'speaking'];
const SECTION_LABELS = { reading: 'Reading', listening: 'Listening', writing: 'Writing', speaking: 'Speaking' };

// Level display names
const LEVEL_NAMES = {
  A1: 'Beginner A1', A2: 'Elementary A2', B1: 'Pre-Intermediate B1', B2: 'Intermediate B2', C1: 'Advanced C1', C2: 'Proficient C2',
  N5: 'N5 - Beginner', N4: 'N4 - Pre-Intermediate', N3: 'N3 - Intermediate', N2: 'N2 - Advanced', N1: 'N1 - Proficient'
};

const FALLBACK_LANGUAGES = [
  { code: 'en', name: 'English', flag: '/images/flag-english.svg', active: false },
  { code: 'fr', name: 'French', flag: '/images/flag-french.svg', active: true },
  { code: 'de', name: 'German', flag: '/images/flag-german.svg', active: true },
  { code: 'es', name: 'Spanish', flag: '/images/flag-spanish.svg', active: true },
  { code: 'it', name: 'Italian', flag: '/images/flag-italian.svg', active: true },
  { code: 'ja', name: 'Japanese', flag: '/images/flag-japanese.svg', active: true }
];

const ExamPrep = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language: stateLanguage, proficiencyLevel: stateLevel } = location.state || {};

  // When opened in a new tab from the Exam Prep List, params come via the URL
  // query string (location.state is empty in a new tab).
  const searchParams = new URLSearchParams(location.search);
  const viewSummaryFromQuery = searchParams.get('viewSummary') === '1';
  const attemptIdFromQuery = searchParams.get('attemptId') || null;
  const langFromQuery = searchParams.get('lang') || null;

  const [language, setLanguage] = useState(stateLanguage || langFromQuery || 'de');
  const [proficiencyLevel, setProficiencyLevel] = useState(stateLevel || 'A1');
  const langName = languageNames[language] || 'German';
  const [fromDashboard, setFromDashboard] = useState(!!(
    (location.state && location.state.viewSummary) || viewSummaryFromQuery
  ));

  // Intro controls
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [langList, setLangList] = useState(FALLBACK_LANGUAGES);

  usePageTimeTracker('exam-prep', user);

  // Load languages for the language switcher
  useEffect(() => {
    axios.get(`${API_BASE}/languages`, { withCredentials: true })
      .then(res => {
        if (res.data && typeof res.data === 'object') {
          const langs = Object.entries(res.data).map(([code, v]) => ({
            code, name: v.label,
            // DB drives active/inactive (English stays disabled if DB says so).
            // Japanese is enabled for Exam Prep (it has its own dedicated page).
            active: code === 'ja' ? true : (v.active !== false && v.active !== 0),
            flag: v.flag || `/images/flag-${v.label.toLowerCase()}.svg`
          }));
          if (langs.length > 0) setLangList(langs);
        }
      }).catch(() => {});
  }, []);

  // View state
  const [view, setView] = useState('intro'); // 'intro' | 'quiz' | 'results'
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Attempts
  const [attemptsRemaining, setAttemptsRemaining] = useState(2);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [canAttempt, setCanAttempt] = useState(true);
  const [attemptId, setAttemptId] = useState(null);

  // Exam config (questions/duration/marks/passing) per language + level — from DB
  const [examConfig, setExamConfig] = useState({
    total_questions: 40, duration_minutes: 60, total_marks: 100, marks_per_skill: 25, passing_marks: 60, section_pass_min: null
  });

  // Exam data
  const [examSessionId, setExamSessionId] = useState(null);
  const [sections, setSections] = useState([]); // ordered array of { key, data }
  const [activeSection, setActiveSection] = useState(0);
  const [currentItem, setCurrentItem] = useState(0); // index within a section's flattened question list

  // Answers: { [q_id]: value }, writing/speaking store agent results separately
  const [answers, setAnswers] = useState({});
  const [writingResults, setWritingResults] = useState({}); // { task_id: {score,...} }
  const [speakingResults, setSpeakingResults] = useState({}); // { task_id: {score,...} }

  // Timer — duration comes from the per-language+level config (duration_minutes).
  // `activeExamDurationRef` captures the duration in effect when the exam started,
  // so the timer bar and time-taken calc stay stable during the quiz.
  const [timeLeft, setTimeLeft] = useState(1800);
  const activeExamDurationRef = useRef(1800);
  const timeTakenRef = useRef(null); // frozen time-taken (seconds) captured on timeout
  // Absolute end timestamp (ms). The countdown is derived from this vs Date.now()
  // so system sleep / tab throttling can't add time (setInterval-only would pause).
  const deadlineRef = useRef(null);
  const prepPauseStartRef = useRef(null); // when the main timer got paused for prep
  const EXAM_DURATION = Math.max(1, Number(examConfig.duration_minutes) || 30) * 60;

  // Popups
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [genError, setGenError] = useState('');

  // Results
  const [results, setResults] = useState(null);
  const [resultReviewTab, setResultReviewTab] = useState('incorrect');

  // Audio (listening)
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [playsLeft, setPlaysLeft] = useState({}); // { audio_id: remaining }
  const audioRef = useRef(null);
  // Scroll container for the current audio's questions — reset to top on page change.
  const listeningQuestionsRef = useRef(null);

  // Speaking recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordings, setRecordings] = useState({}); // { task_id: { blob, url } }

  // Speaking preparation timer (non image/interview tasks). While active, the main
  // exam timer is paused; on Start Recording (or when prep elapses) the main timer resumes.
  const [prepActive, setPrepActive] = useState(false);
  const [prepTimeLeft, setPrepTimeLeft] = useState(0);
  const [prepTaskId, setPrepTaskId] = useState(null); // which task the prep timer belongs to
  const [prepDoneTasks, setPrepDoneTasks] = useState({}); // { task_id: true } prep already consumed/skipped
  const prepTimerRef = useRef(null);
  const activePrepDurationRef = useRef(600); // full prep duration for the current task (for the bar)
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Playback of a completed recording
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const playbackAudioRef = useRef(null);

  // Read-aloud (TTS)
  const [ttsPlayingId, setTtsPlayingId] = useState(null); // id of the item currently being read
  const ttsAudioRef = useRef(null);

  // Speaking dialogue (avatar) — per task_id
  const [dialogueState, setDialogueState] = useState({}); // { task_id: { started, scenarioId, turns: [{role,text}], ended } }
  const [dialogueInput, setDialogueInput] = useState('');
  const [dialogueBusy, setDialogueBusy] = useState(false);

  // Keep dialogueInputRef in sync with state for reliable closure access
  useEffect(() => { dialogueInputRef.current = dialogueInput; }, [dialogueInput]);

  // Close intro dropdowns when clicking outside them
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ep-intro-lang-select')) setShowLangDropdown(false);
      if (!e.target.closest('.ep-intro-level-select')) setShowLevelDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [selectedTopics, setSelectedTopics] = useState({}); // { task_id: [topicId,...] } for AskAnswerQuestions
  const [selectedScenario, setSelectedScenario] = useState({}); // { task_id: scenarioId } for Dialogue/Monologue/Expose
  const [expandedScenario, setExpandedScenario] = useState({}); // { 'task_id:scnId': true } for Expose "View more"
  // Dialogue mic/STT
  const [dialogueListening, setDialogueListening] = useState(false);
  const dialogueTranscriptRef = useRef('');
  const dialogueInputRef = useRef(''); // mirrors dialogueInput state for reliable closure access
  const dialogueRecorderRef = useRef(null);      // MediaRecorder capturing whole conversation audio
  const dialogueChunksRef = useRef([]);           // accumulated audio chunks across all turns
  const dialogueStreamRef = useRef(null);
  const dialogueTurnsRef = useRef({});            // { task_id: [{role,text}] } — always-latest turns
  // Dialogue avatar (BS-style) controls
  const [dlgMicActive, setDlgMicActive] = useState(false);
  const [dlgCameraOn, setDlgCameraOn] = useState(false);
  const [dlgAvatarMuted, setDlgAvatarMuted] = useState(false);
  const [dlgSpeaking, setDlgSpeaking] = useState(false);
  const dlgVideoRef = useRef(null);
  const dlgCamStreamRef = useRef(null);
  const dlgLastReplyRef = useRef('');
  const dlgLogEndRef = useRef(null);
  const dialogueAudioRef = useRef({}); // { task_id: base64 } — full conversation audio, evaluated on Save and Next
  const dialogueStartTsRef = useRef({}); // { task_id: ms } — when recording started, to compute duration

  // Dialogue conversation timer. The max ALWAYS comes from the agent's
  // dialogue_duration_sec[1]; DIALOGUE_MAX_SEC is only a last-resort default used
  // when the task carries no duration at all (so the timer never breaks).
  const DIALOGUE_MAX_SEC = 5 * 60;
  const getDialogueMaxSec = (task) => {
    const dd = task && task.dialogue_duration_sec;
    if (Array.isArray(dd) && Number(dd[1]) > 0) return Number(dd[1]);
    if (Number(dd) > 0) return Number(dd);
    return DIALOGUE_MAX_SEC;
  };
  const [dialogueElapsed, setDialogueElapsed] = useState(0);
  const dialogueTimerRef = useRef(null);

  // Track which items the user has visited/attempted (controls qnav clickability)
  const [visitedItems, setVisitedItems] = useState({}); // { 'section:index': true }
  // Track which items have been SAVED (answer submitted via Save & Next / Skip).
  // A section tab only turns green (Done) once all its items are saved — not just
  // selected/recorded in-progress.
  const [savedItems, setSavedItems] = useState({}); // { item_id: true }
  const [savingItem, setSavingItem] = useState(false);
  const [lastItemDone, setLastItemDone] = useState(false); // last item saved -> show Submit

  const pendingNavRef = useRef(null);
  const timerRef = useRef(null);

  const itemKeyFor = (secIdx, idx) => `${secIdx}:${idx}`;

  // --- Listening audio-paging helpers ---------------------------------------
  // getSectionItems stays question-flattened (scoring/submit/results depend on it).
  // For the listening RENDER + NAV we group those flattened items by audio so that
  // one page shows one audio with ALL its questions (scrollable).
  // Returns [{ audio, questions:[q,...], startIndex, endIndex }] where start/end are
  // indices into the question-flattened sectionItems for that section.
  const getListeningPages = (secItems) => {
    const pages = [];
    let idx = 0;
    secItems.forEach(it => {
      const aid = it.audio?.audio_id;
      const last = pages[pages.length - 1];
      if (last && last.audio?.audio_id === aid) {
        last.questions.push(it.question);
        last.endIndex = idx;
      } else {
        pages.push({ audio: it.audio, questions: [it.question], startIndex: idx, endIndex: idx });
      }
      idx += 1;
    });
    return pages;
  };

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Exam Prep', current: true }
  ];

  // True while the "View Summary" entry effect is loading a past attempt, so the
  // attempts-check effect doesn't clear the loading spinner and flash the intro.
  const viewSummaryModeRef = useRef(!!((location.state && location.state.viewSummary) || viewSummaryFromQuery));

  // ---- Attempts check on mount ----
  useEffect(() => {
    const checkAttempts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/exam-prep/check-attempts?language_code=${language}&level=${mapLevel(proficiencyLevel)}`, { withCredentials: true });
        if (res.data.success) {
          setAttemptsRemaining(res.data.attempts_remaining);
          setAttemptsUsed(res.data.attempts_used);
          setCanAttempt(res.data.can_attempt);
        }
      } catch (err) {
        console.error('Check attempts error:', err);
      }
      // In "View Summary" mode the summary effect controls loading/view.
      if (!viewSummaryModeRef.current) setLoading(false);
    };
    checkAttempts();
  }, [language, proficiencyLevel]);

  // ---- Restore the result view after a browser refresh ----
  // Restore the result page ONLY when the page was refreshed on the result view.
  // A fresh navigation into Exam Prep (from a link on Explore/Home) carries
  // location.state; a hard refresh does NOT. So:
  //   - has location.state  -> fresh entry -> discard stale result, show intro
  //   - no location.state   -> refresh     -> restore the saved result
  useEffect(() => {
    try {
      // In "View Summary" mode (opened from the list page, possibly in a new tab),
      // the viewSummary effect is authoritative — do NOT restore a stale ep_result
      // from a previous exam, which would otherwise override/precede the summary and
      // only correct itself on a manual refresh.
      if ((location.state && location.state.viewSummary) || viewSummaryFromQuery) return;

      const cameFromLink = !!(location.state && (location.state.language || location.state.proficiencyLevel));

      const saved = sessionStorage.getItem('ep_result');
      if (!saved) return;

      if (cameFromLink) {
        // Navigated in via a link -> start clean, drop any old result.
        sessionStorage.removeItem('ep_result');
        return;
      }

      const data = JSON.parse(saved);
      if (data && data.results) {
        if (data.sections) setSections(data.sections);
        if (data.answers) setAnswers(data.answers);
        if (data.language) setLanguage(data.language);
        if (data.proficiencyLevel) setProficiencyLevel(data.proficiencyLevel);
        if (data.attemptId) setAttemptId(data.attemptId);
        if (data.examConfig) setExamConfig(data.examConfig);
        if (data.dialogueTurns) dialogueTurnsRef.current = data.dialogueTurns;
        setResults(data.results);
        setView('results');
        setLoading(false);
      }
    } catch (e) { /* ignore corrupt storage */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- "View Exam Prep Summary" entry (from the Dashboard) ----
  // When navigated with location.state.viewSummary, load the user's most recent
  // COMPLETED attempt for the language and render it in the same result view used
  // right after finishing an exam (section scores + Learn from Every Question).
  useEffect(() => {
    if (!(location.state && location.state.viewSummary) && !viewSummaryFromQuery) return;
    let cancelled = false;
    const loadSummary = async () => {
      try {
        setLoading(true);
        // If the Exam Prep List passed a specific attempt id, load that attempt
        // directly. Otherwise fall back to finding the most recent COMPLETED attempt.
        let attemptId = (location.state && location.state.attemptId) || attemptIdFromQuery || null;
        if (!attemptId) {
          const preferred = (location.state && location.state.language) || langFromQuery || language;
          const allLangs = ['en', 'fr', 'de', 'es', 'it', 'ja'];
          const langOrder = [preferred, ...allLangs.filter(l => l !== preferred)];
          let latest = null;
          for (const lc of langOrder) {
            const h = await axios.get(`${API_BASE}/exam-prep/history?language_code=${lc}&level=A1&_t=${Date.now()}`, { withCredentials: true }).catch(() => null);
            const hist = (h && h.data && h.data.history) || [];
            for (const x of hist) {
              if (x.status !== 'completed') continue;
              const xt = new Date(x.completed_at || x.started_at);
              if (!latest || xt > new Date(latest.completed_at || latest.started_at)) latest = x;
            }
            if (latest && lc === preferred) break;
          }
          if (cancelled || !latest) { viewSummaryModeRef.current = false; setLoading(false); setView('intro'); return; }
          attemptId = latest.id;
        }

        const attRes = await axios.get(`${API_BASE}/exam-prep/attempt/${attemptId}?_t=${Date.now()}`, { withCredentials: true });
        if (cancelled || !attRes.data || !attRes.data.success) { setLoading(false); return; }
        const at = attRes.data.attempt || {};
        const dbAnswers = attRes.data.answers || [];

        // Reflect the attempt's language/level in the header + config.
        if (at.language_code) setLanguage(at.language_code);

        // Parse the STORED agent response (saved when the exam was completed) so the
        // summary shows the full feedback — headline, summary, highlights/strengths,
        // keep-going message, section commentary — not just a generic placeholder.
        let storedAgent = null;
        if (at.agent_response) {
          try {
            const parsed = typeof at.agent_response === 'string' ? JSON.parse(at.agent_response) : at.agent_response;
            // The stored response may be the agent envelope { content: {...} } or the
            // content object directly. Use the content (the result payload).
            storedAgent = parsed && parsed.content ? parsed.content : parsed;
          } catch (e) { storedAgent = null; }
        }

        // LAZY BACKFILL for OLD attempts (completed before feedback was persisted).
        // The agent /submit can't regenerate because the original exam session no
        // longer exists on AgentHub, so we build an accurate feedback object from the
        // stored per-skill scores (deterministic, no agent dependency) and save it so
        // subsequent views are instant and consistent.
        const hasNarrative = storedAgent && (storedAgent.headline || storedAgent.summary || (storedAgent.highlights && storedAgent.highlights.items));
        if (!hasNarrative) {
          const built = buildFeedbackFromScores(at);
          storedAgent = built;
          // Persist so future opens use the stored copy (and so the summary matches
          // on every subsequent view).
          axios.post(`${API_BASE}/exam-prep/save-feedback`, {
            attempt_id: attemptId,
            session_id: at.session_id || '',
            language_code: at.language_code || language,
            level: at.level || proficiencyLevel,
            agent_feedback: built,
          }, { withCredentials: true }).catch(() => {});
        }

        // Rebuild the `results` object in the same shape finalizeAndShowResults uses.
        // Prefer the stored agent fields; fall back to synthetic section scores from
        // the per-skill columns so the section-wise display is always accurate.
        const perSkillMax = Number(at.max_score ? at.max_score / 4 : 25) || 25;
        const syntheticSectionScores = [
          { section: 'Reading', marks: Number(at.reading_score) || 0, max_marks: perSkillMax },
          { section: 'Listening', marks: Number(at.listening_score) || 0, max_marks: perSkillMax },
          { section: 'Writing', marks: Number(at.writing_score) || 0, max_marks: perSkillMax },
          { section: 'Speaking', marks: Number(at.speaking_score) || 0, max_marks: perSkillMax },
        ];
        const agent = {
          ...(storedAgent || {}),
          overall: (storedAgent && storedAgent.overall) || { score: Number(at.score) || 0, out_of: Number(at.max_score) || 100 },
          section_scores: (storedAgent && Array.isArray(storedAgent.section_scores) && storedAgent.section_scores.length)
            ? storedAgent.section_scores
            : syntheticSectionScores,
        };
        const resultData = {
          objective: { correct: Number(at.score) || 0, total: Number(at.total_questions) || 0, perSection: { reading: 0, listening: 0 }, records: [] },
          writingScore: Number(at.writing_score) || 0,
          speakingScore: Number(at.speaking_score) || 0,
          writingResults: {},
          speakingResults: {},
          totalScore: Number(at.score) || 0,
          timeTaken: Number(at.time_taken) || 0,
          agent,
          dbAnswers,
        };
        setResults(resultData);
        setView('results');
        viewSummaryModeRef.current = false;
        setLoading(false);
      } catch (e) {
        viewSummaryModeRef.current = false;
        if (!cancelled) { setLoading(false); setView('intro'); }
      }
    };
    loadSummary();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Exam config (chips) per language + level ----
  useEffect(() => {
    const lvl = mapLevel(proficiencyLevel);
    axios.get(`${API_BASE}/exam-prep/config?language_code=${language}&level=${lvl}&_t=${Date.now()}`, {
      withCredentials: true,
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
    })
      .then(res => { if (res.data && res.data.success && res.data.config) setExamConfig(res.data.config); })
      .catch(() => {});
  }, [language, proficiencyLevel]);

  // ---- Timer ----
  useEffect(() => {
    if (view !== 'quiz') return;
    // Pause the main exam timer while the speaking preparation timer runs.
    // While prep is running the main timer is paused: remember when the pause
    // started so we can push the deadline forward by the paused duration on resume.
    if (prepActive) {
      if (prepPauseStartRef.current == null) prepPauseStartRef.current = Date.now();
      return;
    }
    // Resuming after a prep pause: extend the deadline by however long we paused.
    if (prepPauseStartRef.current != null && deadlineRef.current != null) {
      deadlineRef.current += (Date.now() - prepPauseStartRef.current);
      prepPauseStartRef.current = null;
    }
    if (deadlineRef.current == null) return;
    // Derive the remaining time from the absolute deadline vs now, so system sleep
    // or background-tab throttling can't grant extra time.
    const tick = () => {
      const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        handleAutoSubmit();
      }
    };
    tick(); // sync immediately (also corrects after wake)
    timerRef.current = setInterval(tick, 1000);
    // Re-sync the moment the tab becomes visible again (after sleep/background).
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, prepActive]);

  // Scroll back to the top whenever the current item/section changes, so every
  // new question/passage/audio starts at the top instead of keeping the previous
  // scroll position. The actual scroll container varies (the questions container,
  // the section wrapper, the content card on short screens, or the page), so
  // reset all of them after the new content has rendered.
  useEffect(() => {
    const toTop = () => {
      if (listeningQuestionsRef.current) listeningQuestionsRef.current.scrollTop = 0;
      [
        '.exam-prep-page .ep-reading',
        '.exam-prep-page .ep-listening',
        '.exam-prep-page .ep-listening-questions',
        '.exam-prep-page .ep-writing',
        '.exam-prep-page .ep-speaking',
        '.exam-prep-page .ep-dlg-left',
        '.exam-prep-page .ep-dlg-right',
        '.exam-prep-page.quiz-active .bw-content-card',
        '.exam-prep-page.quiz-active .bw-main',
      ].forEach(sel => {
        const el = document.querySelector(sel);
        if (el) el.scrollTop = 0;
      });
      // The reading/notice passage boxes have their own inner scroll — reset every one.
      // (.ep-passage-box carries overflow-y:auto, so its own scrollTop is the one to reset.)
      document.querySelectorAll('.exam-prep-page .ep-passage-box').forEach(el => { el.scrollTop = 0; });
      try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
    };
    // Reset immediately, then again across two animation frames so the scroll
    // reset also lands AFTER the new (possibly taller) passage has been laid out.
    toTop();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      toTop();
      raf2 = requestAnimationFrame(toTop);
    });
    return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
  }, [activeSection, currentItem]);

  // Reset the preparation timer when moving to a different item/section (so a paused
  // main timer never leaks onto another question), then auto-start it if the new item
  // is a prep-eligible speaking task whose prep hasn't been used yet.
  useEffect(() => {
    if (prepTimerRef.current) clearInterval(prepTimerRef.current);
    setPrepActive(false);
    setPrepTimeLeft(0);
    setPrepTaskId(null);

    if (view !== 'quiz') return;
    const sec = sections[activeSection];
    if (!sec || sec.key !== 'speaking') return;
    const items = getSectionItems(sec);
    const curTask = items[currentItem]?.task;
    if (!curTask) return;
    const secs = getPrepSeconds(curTask);
    // No prep for interview/image/ask.
    if (secs <= 0) return;
    // Don't restart prep if it was already used, the task is recorded, or a
    // dialogue conversation has already started/ended.
    if (prepDoneTasks[curTask.task_id] || recordings[curTask.task_id]) return;
    if (dialogueState[curTask.task_id]?.started || dialogueState[curTask.task_id]?.ended) return;
    activePrepDurationRef.current = secs;
    setPrepTaskId(curTask.task_id);
    setPrepTimeLeft(secs);
    setPrepActive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, currentItem, view]);

  // ---- Speaking preparation timer ----
  // Counts down while active. When it reaches 0, prep stops automatically and the
  // main exam timer resumes (handled by the effect above via prepActive flag).
  useEffect(() => {
    if (!prepActive) return;
    prepTimerRef.current = setInterval(() => {
      setPrepTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(prepTimerRef.current);
          // Mark this task's prep as consumed and resume the main timer.
          setPrepActive(false);
          setPrepDoneTasks(d => (prepTaskId ? { ...d, [prepTaskId]: true } : d));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (prepTimerRef.current) clearInterval(prepTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepActive, prepTaskId]);

  // ---- Prevent accidental navigation during quiz ----
  useEffect(() => {
    if (view !== 'quiz') return;
    const handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = 'Your exam prep progress will be lost.'; };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => { window.history.pushState(null, '', window.location.href); setShowLeaveConfirm(true); };
    window.addEventListener('popstate', handlePopState);
    // Intercept in-app link clicks (nav, footer, etc.) so leaving mid-exam shows
    // the same confirm popup as BrainWarmup / PostAssessment.
    const handleLinkClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (anchor && anchor.getAttribute('href') && !anchor.getAttribute('href').startsWith('#')) {
        e.preventDefault();
        e.stopPropagation();
        pendingNavRef.current = anchor.getAttribute('href');
        setShowLeaveConfirm(true);
      }
    };
    document.addEventListener('click', handleLinkClick, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [view]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    // Show H:MM:SS once the exam is an hour or longer (e.g. 1:30:00); otherwise MM:SS.
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };
  // Recorded playback label shown in seconds (e.g. "180 s Recorded").
  const fmtRecorded = (s) => {
    const secs = Math.max(0, Number(s) || 0);
    return `${secs} s Recorded`;
  };
  const timerProgress = (timeLeft / (activeExamDurationRef.current || EXAM_DURATION)) * 100;

  // Human-friendly exam duration for the intro chip (e.g. "1 hour", "1 hour 15 min", "45 min").
  const formatDuration = (minutes) => {
    const m = Number(minutes) || 0;
    if (m <= 0) return '—';
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (h === 0) return `${rem} min`;
    const hourLabel = `${h} hour${h > 1 ? 's' : ''}`;
    return rem === 0 ? hourLabel : `${hourLabel} ${rem} min`;
  };

  // ---- Build ordered sections from agent response ----
  const buildSections = (sectionsObj) => {
    const ordered = [];
    SECTION_ORDER.forEach(key => {
      const data = sectionsObj[key];
      if (!data) return;
      // Skip sections the agent failed to parse (writing sometimes returns { error })
      if (data.error) {
        ordered.push({ key, data: { ...data, unavailable: true } });
      } else {
        ordered.push({ key, data });
      }
    });
    // Ensure EVERY reading/listening question has a UNIQUE q_id. The agent
    // sometimes reuses a q_id across questions; because the radio group name and
    // the answer key are both q_id, duplicates make selecting one question's
    // option visually select another (auto-selected answers bug). Suffix any
    // repeat so each question is an independent radio group / answer slot.
    const seen = new Set();
    const uniquify = (q) => {
      if (!q) return;
      let id = q.q_id != null ? String(q.q_id) : `q_${seen.size}`;
      if (seen.has(id)) {
        let n = 2;
        while (seen.has(`${id}#${n}`)) n++;
        id = `${id}#${n}`;
      }
      seen.add(id);
      q.q_id = id;
    };
    ordered.forEach(({ key, data }) => {
      if (key === 'reading') (data.passages || []).forEach(p => (p.questions || []).forEach(uniquify));
      if (key === 'listening') (data.audios || []).forEach(a => (a.questions || []).forEach(uniquify));
    });
    return ordered;
  };

  // Flatten reading passages -> questions, listening audios -> questions
  const getSectionItems = (section) => {
    if (!section) return [];
    const { key, data } = section;
    if (!data || data.unavailable) return [];
    if (key === 'reading') {
      const items = [];
      (data.passages || []).forEach(p => {
        (p.questions || []).forEach(q => items.push({ type: 'reading', passage: p, question: q }));
      });
      return items;
    }
    if (key === 'listening') {
      const items = [];
      (data.audios || []).forEach(a => {
        (a.questions || []).forEach(q => items.push({ type: 'listening', audio: a, question: q }));
      });
      return items;
    }
    if (key === 'writing') {
      return (data.tasks || []).map(t => ({ type: 'writing', task: t }));
    }
    if (key === 'speaking') {
      return (data.tasks || []).map(t => ({ type: 'speaking', task: t }));
    }
    return [];
  };

  const currentSection = sections[activeSection] || null;
  const sectionItems = getSectionItems(currentSection);
  const item = sectionItems[currentItem] || null;

  // Marks come from the agent response. Read the value from whichever field the
  // agent used; return null when the agent didn't provide marks (so we can hide
  // the badge instead of showing a fabricated "1").
  const getMarks = (obj) => {
    if (!obj) return null;
    const keys = ['marks', 'mark', 'max_marks', 'total_marks', 'points', 'point', 'max_score', 'question_marks', 'weight', 'score_weight'];
    for (const k of keys) {
      if (obj[k] != null && obj[k] !== '' && !Number.isNaN(Number(obj[k]))) return Number(obj[k]);
    }
    return null;
  };
  const marksLabel = (n) => `${n} Mark${n > 1 ? 's' : ''}`;

  // Reading/listening questions carry no marks from the agent, so each counts
  // as 1 mark.
  const READING_LISTENING_MARK = 1;

  // Mark the current item as visited whenever it changes (so its qnav box stays clickable)
  useEffect(() => {
    if (view === 'quiz') {
      setVisitedItems(prev => ({ ...prev, [`${activeSection}:${currentItem}`]: true }));
    }
    // Reset the "last item done" flag when navigating back to any earlier item
    setLastItemDone(false);
    // Stop any recording playback when moving to another item
    if (playbackAudioRef.current) { playbackAudioRef.current.pause(); playbackAudioRef.current = null; }
    setIsPlayingBack(false);
    setPlaybackProgress(0);
    // Stop read-aloud when moving to another item
    if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    setTtsPlayingId(null);
    // Stop avatar speech / camera when moving away
    stopAvatarSpeech();
    setDlgSpeaking(false);
    if (dlgCamStreamRef.current) { dlgCamStreamRef.current.getTracks().forEach(t => t.stop()); dlgCamStreamRef.current = null; setDlgCameraOn(false); }
    // Stop any conversation recorder + its mic stream when navigating away
    if (dialogueRecorderRef.current && dialogueRecorderRef.current.state !== 'inactive') { try { dialogueRecorderRef.current.stop(); } catch (e) { /* noop */ } }
    dialogueRecorderRef.current = null;
    if (dialogueStreamRef.current) { dialogueStreamRef.current.getTracks().forEach(t => t.stop()); dialogueStreamRef.current = null; }
    if (dialogueTimerRef.current) { clearInterval(dialogueTimerRef.current); dialogueTimerRef.current = null; }
  }, [view, activeSection, currentItem]);

  // Preload the current listening audio's metadata so the time shows the real
  // total (e.g. "0:00 / 0:30") before the user presses play.
  useEffect(() => {
    if (view !== 'quiz') return;
    const sec = sections[activeSection];
    if (!sec || sec.key !== 'listening') return;
    const items = getSectionItems(sec);
    const audio = items[currentItem]?.audio;
    if (!audio) return;
    // Seed from any duration hint the agent sent
    const hint = Number(audio.duration || audio.duration_seconds || 0);
    setAudioCurrentTime(0);
    if (hint > 0) { setAudioDuration(hint); return; }
    if (!audio.audio_url) { setAudioDuration(0); return; }
    // Otherwise load just the metadata to read the real duration
    const probe = new Audio();
    probe.preload = 'metadata';
    probe.src = audio.audio_url;
    const onMeta = () => { if (isFinite(probe.duration) && probe.duration > 0) setAudioDuration(probe.duration); };
    probe.addEventListener('loadedmetadata', onMeta);
    return () => { probe.removeEventListener('loadedmetadata', onMeta); probe.src = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeSection, currentItem, sections]);



  // ---- Start exam ----
  const handleStartExam = async () => {
    setGenError('');
    setGenerating(true);
    // Starting a new exam discards any previously saved result.
    try { sessionStorage.removeItem('ep_result'); } catch (e) { /* noop */ }
    try {
      // Record the attempt (enforces 2 per 24h per language + level)
      const startRes = await axios.post(`${API_BASE}/exam-prep/start`, { language_code: language, level: mapLevel(proficiencyLevel) }, { withCredentials: true });
      if (!startRes.data.success) {
        setCanAttempt(false);
        setGenerating(false);
        return;
      }
      setAttemptId(startRes.data.attempt_id);
      setAttemptsRemaining(startRes.data.attempts_remaining);

      // Normalize to the agent's expected format: language name + CEFR code (A1/A2/N5...)
      const agentLanguage = mapLanguage(language);   // 'de' -> 'German'
      const agentLevel = mapLevel(proficiencyLevel);  // 'beginner-a1' -> 'A1'

      // Generate the exam from the agent
      const genRes = await axios.post(`${API_BASE}/exam-prep/generate`, {
        language: agentLanguage,
        level: agentLevel,
        session_id: null
      }, { withCredentials: true });

      // The generate response is encrypted; decrypt it into the agent data object.
      const genData = await readEpResponse(genRes.data);
      const payload = genData?.content || genData;
      if (!payload || !payload.sections) {
        setGenError('The exam could not be generated. Please try again.');
        setGenerating(false);
        return;
      }

      const sessId = payload.session_id || null;
      setExamSessionId(sessId);
      const ordered = buildSections(payload.sections);
      setSections(ordered);

      // Persist the full generated exam response
      axios.post(`${API_BASE}/exam-prep/save-exam`, {
        attempt_id: startRes.data.attempt_id,
        session_id: sessId,
        language_code: language,
        language: agentLanguage,
        level: agentLevel,
        full_response: payload
      }, { withCredentials: true }).catch(e => console.error('Save exam error:', e));

      // Init plays-left for listening audios
      const pl = {};
      const listening = payload.sections.listening;
      if (listening && listening.audios) {
        // Each listening audio must always be playable twice, regardless of what
        // the agent sends for play_count (SME requirement).
        listening.audios.forEach(a => { pl[a.audio_id] = 2; });
      }
      setPlaysLeft(pl);

      setActiveSection(0);
      setCurrentItem(0);
      setAnswers({});
      setVisitedItems({ '0:0': true });
      setWritingResults({});
      setSpeakingResults({});
      // Exam duration comes from the per-language + level config (duration_minutes),
      // resolved into EXAM_DURATION (seconds). No hardcoded testing value.
      const durationSecs = EXAM_DURATION;
      activeExamDurationRef.current = durationSecs;
      deadlineRef.current = Date.now() + durationSecs * 1000;
      setTimeLeft(durationSecs);
      setView('quiz');
    } catch (err) {
      if (err.response?.status === 429) {
        setCanAttempt(false);
        setAttemptsRemaining(0);
      } else {
        setGenError('Something went wrong generating the exam. Please try again.');
      }
    }
    setGenerating(false);
  };

  // ---- Answer selection — only updates local state; saved on "Save and Next" ----
  const selectAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  // Read the score from a writing/speaking evaluation. The agent now returns
  // `final_score`; older responses used `score`. Returns null when neither is set.
  const getEvalScore = (evaluation) => {
    if (!evaluation) return null;
    if (evaluation.final_score != null) return Number(evaluation.final_score);
    if (evaluation.score != null) return Number(evaluation.score);
    return null;
  };

  // Update one field of a FormFilling writing task (answer stored as an object { fieldId: value })
  const setFormField = (taskId, fieldId, value) => {
    setAnswers(prev => {
      const current = (prev[taskId] && typeof prev[taskId] === 'object') ? prev[taskId] : {};
      return { ...prev, [taskId]: { ...current, [fieldId]: value } };
    });
  };

  // Evaluate + persist the CURRENT item (reading/listening scored inline; writing via agent)
  const saveCurrentItem = async () => {
    if (!item) return;
    const key = itemKeyFor(activeSection, currentItem);
    setVisitedItems(prev => ({ ...prev, [key]: true }));

    // LISTENING — one page = one audio. Save ALL its questions: answered ones are
    // scored; any unanswered question is recorded as skipped so the bottom box
    // reflects it and results count it as not attempted.
    if (sectionKey === 'listening') {
      const page = getListeningPages(sectionItems).find(p => currentItem >= p.startIndex && currentItem <= p.endIndex);
      if (!page) return;
      const records = [];
      const skippedIds = [];
      page.questions.forEach(qq => {
        const val = answers[qq.q_id];
        const opts = qq.options || [];
        if (!val || val === '__skipped__') {
          // Unanswered -> record as skipped (server marks it not correct)
          skippedIds.push(qq.q_id);
          records.push({
            section: 'listening', item_id: qq.q_id, item_number: qq.question_num,
            question_text: questionText(qq), options: opts,
            correct_answer: qq.correct_answer ?? null, explanation: qq.explanation ?? null,
            user_answer: '__skipped__', is_skipped: true,
            audio_url: page.audio?.audio_url || null, max_score: 1
          });
          return;
        }
        // Answer only — server grades against the answer key.
        records.push({
          section: 'listening', item_id: qq.q_id, item_number: qq.question_num,
          question_text: questionText(qq), options: opts,
          correct_answer: qq.correct_answer ?? null, explanation: qq.explanation ?? null,
          user_answer: val, is_skipped: false,
          audio_url: page.audio?.audio_url || null, max_score: 1
        });
      });
      // Mark unanswered questions skipped in state so the qnav box shows skipped
      if (skippedIds.length) {
        setAnswers(prev => {
          const next = { ...prev };
          skippedIds.forEach(id => { if (!next[id] || next[id] === '__skipped__') next[id] = '__skipped__'; });
          return next;
        });
      }
      if (!records.length) return;
      try {
        await axios.post(`${API_BASE}/exam-prep/save-answers`, {
          attempt_id: attemptId, session_id: examSessionId, answers: records
        }, { withCredentials: true });
      } catch (e) { console.error('Save answer error:', e); }
      return;
    }

    // READING — send the answer; the server grades it against the answer key
    // (correct answers are never sent to the browser).
    if (sectionKey === 'reading' && item.question) {
      const qq = item.question;
      const val = answers[qq.q_id];
      if (!val) return; // nothing selected, nothing to save (skip handles skipped)
      const record = {
        section: sectionKey, item_id: qq.q_id, item_number: qq.question_num,
        question_text: questionText(qq), options: qq.options || [],
        correct_answer: qq.correct_answer ?? null, explanation: qq.explanation ?? null,
        user_answer: val, is_skipped: false,
        audio_url: item.audio?.audio_url || null, max_score: 1
      };
      try {
        await axios.post(`${API_BASE}/exam-prep/save-answers`, {
          attempt_id: attemptId, session_id: examSessionId, answers: [record]
        }, { withCredentials: true });
      } catch (e) { console.error('Save answer error:', e); }
      return;
    }

    // WRITING — evaluate via agent, then save the record with evaluation
    if (sectionKey === 'writing' && item.task) {
      const task = item.task;
      const userAnswer = answers[task.task_id];
      const isEmpty = !userAnswer || userAnswer === '__skipped__'
        || (typeof userAnswer === 'object' && Object.keys(userAnswer).length === 0)
        || (typeof userAnswer === 'string' && userAnswer.trim() === '');
      if (isEmpty) return;
      let evaluation = null;
      try {
        const res = await axios.post(`${API_BASE}/exam-prep/writing/evaluate`, {
          session_id: examSessionId, task_id: task.task_id, user_answer: userAnswer
        }, { withCredentials: true });
        // Response is encrypted; decrypt then unwrap the agent's data.content.
        if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; }
        // cache so results screen can aggregate
        if (evaluation) setWritingResults(prev => ({ ...prev, [task.task_id]: evaluation }));
      } catch (e) { console.error('Writing eval error:', e); }
      const record = {
        section: 'writing', item_id: task.task_id, item_number: task.task_num,
        question_text: task.task_instruction || task.task_label,
        user_answer: typeof userAnswer === 'object' ? JSON.stringify(userAnswer) : userAnswer,
        is_skipped: false, evaluation,
        score: getEvalScore(evaluation),
        max_score: task.marks != null ? task.marks : null
      };
      try {
        await axios.post(`${API_BASE}/exam-prep/save-answers`, {
          attempt_id: attemptId, session_id: examSessionId, answers: [record]
        }, { withCredentials: true });
      } catch (e) { console.error('Save writing answer error:', e); }
      return;
    }

    // SPEAKING (single recording: Interview / AskAnswerQuestions) — send base64 to evaluate
    if (sectionKey === 'speaking' && item.task && item.task.mode !== 'avatar_dialogue' && item.task.task_type !== 'Dialogue') {
      const task = item.task;
      const rec = recordings[task.task_id];
      if (!rec) return; // nothing recorded
      // Clear any stale '__skipped__' marker (user skipped earlier, then recorded)
      // so the qnav box shows Done, not Skipped.
      if (answers[task.task_id] === '__skipped__') {
        setAnswers(prev => { const n = { ...prev }; delete n[task.task_id]; return n; });
      }
      // Reuse the cached evaluation if this recording was already evaluated (e.g.
      // the user went Previous then Save & Next again) — don't re-hit the agent,
      // the same result stands since recordings can't be changed.
      let evaluation = speakingResults[task.task_id] || null;
      if (!evaluation) {
        try {
          const audio_base64 = await blobToBase64(rec.blob);
          const res = await axios.post(`${API_BASE}/exam-prep/speaking/evaluate`, {
            session_id: examSessionId, task_id: task.task_id, audio_base64
          }, { withCredentials: true });
          if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; }
          if (evaluation) setSpeakingResults(prev => ({ ...prev, [task.task_id]: evaluation }));
        } catch (e) { console.error('Speaking eval error:', e); }
      }
      const selectedTopicIds = selectedTopics[task.task_id] || [];
      const record = {
        section: 'speaking', item_id: task.task_id, item_number: task.task_num,
        question_text: task.task_instruction || task.task_label,
        user_answer: JSON.stringify({
          task_type: task.task_type,
          response: 'audio_recorded',
          selected_topics: selectedTopicIds
        }),
        is_skipped: false, evaluation,
        score: getEvalScore(evaluation),
        max_score: task.marks != null ? task.marks : null
      };
      try {
        await axios.post(`${API_BASE}/exam-prep/save-answers`, {
          attempt_id: attemptId, session_id: examSessionId, answers: [record]
        }, { withCredentials: true });
      } catch (e) { console.error('Save speaking answer error:', e); }
      return;
    }

    // SPEAKING DIALOGUE — on Save & Next, evaluate the full conversation audio (base64)
    if (sectionKey === 'speaking' && item.task && (item.task.mode === 'avatar_dialogue' || item.task.task_type === 'Dialogue')) {
      const task = item.task;
      const audio_base64 = dialogueAudioRef.current[task.task_id];
      const turns = dialogueTurnsRef.current[task.task_id] || (dialogueState[task.task_id]?.turns) || [];
      // If no conversation happened, skip evaluation
      if (turns.length === 0) return;
      let evaluation = speakingResults[task.task_id] || null;
      if (!evaluation) {
        try {
          // Send audio_base64 if we captured it; the agent also has the server-side transcript
          const payload = { session_id: examSessionId, task_id: task.task_id };
          if (audio_base64) payload.audio_base64 = audio_base64;
          const res = await axios.post(`${API_BASE}/exam-prep/speaking/evaluate`, payload, { withCredentials: true });
          if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; }
          if (evaluation) setSpeakingResults(prev => ({ ...prev, [task.task_id]: evaluation }));
        } catch (e) { console.error('Dialogue eval error:', e); }
      }
      const record = {
        section: 'speaking', item_id: task.task_id, item_number: task.task_num,
        question_text: task.task_instruction || task.task_label,
        user_answer: JSON.stringify({ task_type: 'Dialogue', scenario_id: dialogueState[task.task_id]?.scenarioId, conversation: turns }),
        is_skipped: false, evaluation,
        score: getEvalScore(evaluation),
        max_score: task.marks != null ? task.marks : null
      };
      try {
        await axios.post(`${API_BASE}/exam-prep/save-answers`, {
          attempt_id: attemptId, session_id: examSessionId, answers: [record]
        }, { withCredentials: true });
      } catch (e) { console.error('Save dialogue answer error:', e); }
    }
  };

  // ---- Navigation within/between sections ----
  // For listening, we step by audio-page (jump to the first question of the next audio).
  const goNext = () => {
    stopAudio();
    if (sectionKey === 'listening') {
      const pages = getListeningPages(sectionItems);
      const cur = pages.find(p => currentItem >= p.startIndex && currentItem <= p.endIndex);
      const curIdx = cur ? pages.indexOf(cur) : -1;
      if (curIdx > -1 && curIdx < pages.length - 1) {
        setCurrentItem(pages[curIdx + 1].startIndex);
        return;
      }
      if (activeSection < sections.length - 1) {
        setActiveSection(activeSection + 1);
        setCurrentItem(0);
      }
      return;
    }
    if (currentItem < sectionItems.length - 1) {
      setCurrentItem(currentItem + 1);
    } else if (activeSection < sections.length - 1) {
      setActiveSection(activeSection + 1);
      setCurrentItem(0);
    }
  };

  // Mark the current item (or all questions of the current listening audio) as
  // SAVED, so its section tab can turn green only after submission.
  const markCurrentSaved = () => {
    const ids = [];
    if (sectionKey === 'listening') {
      const page = getListeningPages(sectionItems).find(p => currentItem >= p.startIndex && currentItem <= p.endIndex);
      (page?.questions || []).forEach(qq => ids.push(qq.q_id));
    } else if (item?.question) {
      ids.push(item.question.q_id);
    } else if (item?.task) {
      ids.push(item.task.task_id);
    }
    if (ids.length) setSavedItems(prev => { const next = { ...prev }; ids.forEach(id => { next[id] = true; }); return next; });
  };

  // "Save and Next" — evaluate/persist current answer, mark visited, then advance
  const saveAndNext = async () => {
    setSavingItem(true);
    await saveCurrentItem();
    markCurrentSaved();
    setSavingItem(false);
    goNext();
  };

  const goPrev = () => {
    stopAudio();
    if (sectionKey === 'listening') {
      const pages = getListeningPages(sectionItems);
      const cur = pages.find(p => currentItem >= p.startIndex && currentItem <= p.endIndex);
      const curIdx = cur ? pages.indexOf(cur) : -1;
      if (curIdx > 0) { setCurrentItem(pages[curIdx - 1].startIndex); return; }
      if (activeSection > 0) {
        const prevSection = sections[activeSection - 1];
        const prevItems = getSectionItems(prevSection);
        setActiveSection(activeSection - 1);
        setCurrentItem(Math.max(0, prevItems.length - 1));
      }
      return;
    }
    if (currentItem > 0) {
      setCurrentItem(currentItem - 1);
    } else if (activeSection > 0) {
      const prevSection = sections[activeSection - 1];
      const prevItems = getSectionItems(prevSection);
      setActiveSection(activeSection - 1);
      setCurrentItem(Math.max(0, prevItems.length - 1));
    }
  };

  const goToSection = (idx) => {
    stopAudio();
    setActiveSection(idx);
    setCurrentItem(0);
  };

  // Mark current item skipped, persist a skipped record, and advance
  const skipCurrent = async () => {
    // Guard against multiple clicks while a skip (esp. its evaluate request for
    // writing/speaking) is still in flight. savingItem also disables the Skip
    // button in the UI, so the user can't fire the same skip twice.
    if (savingItem) return;
    setSavingItem(true);
    try {
      await runSkipCurrent();
    } finally {
      setSavingItem(false);
    }
  };

  const runSkipCurrent = async () => {
    // If a speaking recording is in progress when Skip is clicked, stop it (and its
    // timer) and discard it — skip is a null-answer submission, not a recording.
    if (isRecording) {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        // Prevent the onstop handler from saving this recording.
        mediaRecorderRef.current.onstop = null;
        try { mediaRecorderRef.current.stop(); } catch (e) { /* noop */ }
        try { mediaRecorderRef.current.stream?.getTracks?.().forEach(t => t.stop()); } catch (e) { /* noop */ }
      }
      setIsRecording(false);
      setRecordingTime(0);
      if (item?.task) {
        setRecordings(prev => { const n = { ...prev }; delete n[item.task.task_id]; return n; });
      }
    }

    // LISTENING — skip the whole audio page: mark every unanswered question skipped
    if (sectionKey === 'listening') {
      const page = getListeningPages(sectionItems).find(p => currentItem >= p.startIndex && currentItem <= p.endIndex);
      if (page) {
        const records = [];
        // Skipping an audio clears ALL selections on that page and marks every
        // question skipped — even ones the user had answered before (skip wins).
        setAnswers(prev => {
          const next = { ...prev };
          page.questions.forEach(qq => { next[qq.q_id] = '__skipped__'; });
          return next;
        });
        page.questions.forEach(qq => {
          records.push({
            section: 'listening', item_id: qq.q_id, item_number: qq.question_num,
            question_text: questionText(qq), options: qq.options || [],
            correct_answer: qq.correct_answer ?? null, explanation: qq.explanation ?? null,
            user_answer: '__skipped__', is_skipped: true,
            audio_url: page.audio?.audio_url || null, max_score: 1
          });
        });
        setVisitedItems(prev => ({ ...prev, [itemKeyFor(activeSection, currentItem)]: true }));
        if (records.length) {
          axios.post(`${API_BASE}/exam-prep/save-answers`, {
            attempt_id: attemptId, session_id: examSessionId, answers: records
          }, { withCredentials: true }).catch(e => console.error('Save skip error:', e));
        }
      }
      markCurrentSaved();
      if (isLastItemOverall) setLastItemDone(true);
      else goNext();
      return;
    }

    const key = item?.question?.q_id || item?.task?.task_id;
    if (key) setAnswers(prev => ({ ...prev, [key]: '__skipped__' }));
    // NOTE: a COMPLETED speaking recording is intentionally KEPT when skipping —
    // the user can return via Previous and either submit that same audio (Save and
    // Next) or skip it again. Only an in-progress recording is discarded (handled
    // above in the isRecording block).
    setVisitedItems(prev => ({ ...prev, [itemKeyFor(activeSection, currentItem)]: true }));

    // Reading skip — save a plain skipped record (no agent evaluation).
    if (item && item.question) {
      const q = item.question;
      const record = {
        section: sectionKey, item_id: q.q_id, item_number: q.question_num,
        question_text: questionText(q), options: q.options || [],
        correct_answer: q.correct_answer ?? null, explanation: q.explanation ?? null,
        user_answer: '__skipped__', is_skipped: true,
        audio_url: item.audio?.audio_url || null, max_score: 1
      };
      axios.post(`${API_BASE}/exam-prep/save-answers`, {
        attempt_id: attemptId, session_id: examSessionId, answers: [record]
      }, { withCredentials: true }).catch(e => console.error('Save skip error:', e));
      markCurrentSaved();
      if (isLastItemOverall) setLastItemDone(true); else goNext();
      return;
    }

    // Writing / Speaking skip — hit the evaluate agent with a null answer so the
    // agent returns feedback + a model answer, then save that with is_skipped=true
    // so the result page shows it just like an attempted-but-wrong item.
    if (item && item.task) {
      const task = item.task;
      let evaluation = null;
      try {
        if (sectionKey === 'writing') {
          // FormFilling -> object with every field null; free text -> null.
          let userAnswer = null;
          if (task.task_type === 'FormFilling' && task.form && Array.isArray(task.form.fields)) {
            userAnswer = {};
            task.form.fields.forEach(f => { if (f && f.id != null && f.editable && f.target) userAnswer[f.id] = null; });
            if (Object.keys(userAnswer).length === 0) userAnswer = null;
          }
          const res = await axios.post(`${API_BASE}/exam-prep/writing/evaluate`, {
            session_id: examSessionId, task_id: task.task_id, user_answer: userAnswer
          }, { withCredentials: true });
          if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; }
          if (evaluation) setWritingResults(prev => ({ ...prev, [task.task_id]: evaluation }));
        } else if (sectionKey === 'speaking') {
          const res = await axios.post(`${API_BASE}/exam-prep/speaking/evaluate`, {
            session_id: examSessionId, task_id: task.task_id, audio_base64: null
          }, { withCredentials: true });
          if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; }
          // Only cache the skip (null-audio) evaluation into speakingResults when
          // there is NO completed recording for this task. If a recording exists,
          // the user may return via Previous and submit that audio via Save and
          // Next — which must re-evaluate the real recording, not reuse this
          // skip result. The skip's DB record still carries this evaluation.
          if (evaluation && !recordings[task.task_id]) {
            setSpeakingResults(prev => ({ ...prev, [task.task_id]: evaluation }));
          }
        }
      } catch (e) { console.error('Skip evaluate error:', e); }

      const record = {
        section: sectionKey, item_id: task.task_id, item_number: task.task_num,
        question_text: task.task_instruction || task.task_label,
        user_answer: '__skipped__', is_skipped: true, evaluation,
        score: 0, // skipped = no marks
        max_score: task.marks != null ? task.marks : null
      };
      axios.post(`${API_BASE}/exam-prep/save-answers`, {
        attempt_id: attemptId, session_id: examSessionId, answers: [record]
      }, { withCredentials: true }).catch(e => console.error('Save skip error:', e));
    }
    markCurrentSaved();
    // On the last item of the exam, skipping should reveal Submit (Previous + Submit);
    // otherwise advance to the next item.
    if (isLastItemOverall) setLastItemDone(true);
    else goNext();
  };

  // On the last section, the "last item" for listening is the last audio page's first question.
  const isLastItemOverall = (() => {
    if (activeSection !== sections.length - 1) return false;
    if (currentSection?.key === 'listening') {
      const pages = getListeningPages(sectionItems);
      const lastPage = pages[pages.length - 1];
      return lastPage ? currentItem >= lastPage.startIndex && currentItem <= lastPage.endIndex : true;
    }
    return currentItem === sectionItems.length - 1;
  })();

  // ---- Audio playback (listening) ----
  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setIsPlaying(false);
    setAudioProgress(0);
    setAudioCurrentTime(0);
    setAudioDuration(0);
  };

  const handlePlayAudio = (audio) => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    const remaining = playsLeft[audio.audio_id];
    if (remaining !== undefined && remaining <= 0 && !audioRef.current) return;
    if (!audio.audio_url) return;

    // Audio and Read Aloud are mutually exclusive — stop any TTS before playing.
    stopTts();

    const a = new Audio(audio.audio_url);
    audioRef.current = a;
    // Prime duration from metadata (also seed from any duration hint on the audio)
    const hintDuration = Number(audio.duration || audio.duration_seconds || 0);
    if (hintDuration > 0) setAudioDuration(hintDuration);
    a.onloadedmetadata = () => { if (isFinite(a.duration) && a.duration > 0) setAudioDuration(a.duration); };
    a.ontimeupdate = () => {
      setAudioCurrentTime(a.currentTime);
      if (isFinite(a.duration) && a.duration > 0) {
        setAudioDuration(a.duration);
        setAudioProgress((a.currentTime / a.duration) * 100);
      }
    };
    a.onended = () => { setIsPlaying(false); setAudioProgress(100); if (isFinite(a.duration)) setAudioCurrentTime(a.duration); audioRef.current = null; };
    a.onerror = () => setIsPlaying(false);
    a.play().then(() => {
      setPlaysLeft(prev => ({ ...prev, [audio.audio_id]: Math.max(0, (prev[audio.audio_id] ?? 2) - 1) }));
    }).catch(() => setIsPlaying(false));
    setIsPlaying(true);
  };

  // ---- Speaking preparation timer controls ----
  // Preparation time per task type (seconds). 0 = no preparation time.
  const getPrepSeconds = (task) => {
    if (!task) return 0;
    // Preparation time is FIXED on the frontend (NOT taken from the agent's
    // prep_time_sec). Interview, Image Description, and Ask Questions have no prep
    // time; Exposé gets 30 minutes; all other speaking types get a fixed 10 minutes.
    const isImageTask = task.task_type === 'ImageDescription' || task.mode === 'single_recording_with_image' || task.image_required;
    if (task.task_type === 'Interview' || task.task_type === 'AskAnswerQuestions' || isImageTask) return 0;
    if (task.task_type === 'Expose') return 30 * 60;
    return 10 * 60;
  };

  // Stops the prep countdown early (e.g. when the user starts recording) and
  // resumes the main exam timer.
  const stopPrep = (taskId) => {
    if (prepTimerRef.current) clearInterval(prepTimerRef.current);
    setPrepActive(false);
    if (taskId) setPrepDoneTasks(d => ({ ...d, [taskId]: true }));
  };

  // ---- Speaking recording ----
  const startRecording = async (taskId, maxSeconds = 120) => {
    // Starting the recording ends any running preparation time and resumes the main timer.
    stopPrep(taskId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      const recStartTs = Date.now();
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        // Never report more than the allocated time — cap at maxSeconds.
        const elapsed = Math.round((Date.now() - recStartTs) / 1000);
        const secs = Math.min(elapsed, maxSeconds);
        setRecordings(prev => ({ ...prev, [taskId]: { blob, url: URL.createObjectURL(blob), time: secs } }));
        // A brand-new recording invalidates any cached evaluation for this task
        // (e.g. the user skipped first — which cached a null-audio result — then
        // returned and recorded). Clearing it forces Save and Next to re-evaluate
        // the real audio via the agent instead of reusing the stale skip result.
        setSpeakingResults(prev => { if (!prev[taskId]) return prev; const n = { ...prev }; delete n[taskId]; return n; });
        setAnswers(prev => (prev[taskId] === '__skipped__' ? (() => { const n = { ...prev }; delete n[taskId]; return n; })() : prev));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          // Stop exactly at maxSeconds — never let the counter exceed it.
          if (prev + 1 >= maxSeconds) { stopRecording(); return maxSeconds; }
          return prev + 1;
        });
      }, 1000);
    } catch (e) {
      console.error('Mic access error:', e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setIsRecording(false);
  };

  const blobToBase64 = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(blob);
  });

  // ---- Read aloud (TTS) ----
  const stopTts = () => {
    if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    setTtsPlayingId(null);
  };

  // Reads the given text aloud. `id` identifies the element clicked (so its label toggles to Stop).
  const handleReadAloud = async (id, text) => {
    // If this one is already playing, stop it
    if (ttsPlayingId === id) { stopTts(); return; }
    // Stop any other TTS and the listening audio — only one may play at a time.
    stopTts();
    stopAudio();
    if (!text || !text.trim()) return;
    try {
      const res = await fetch('/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: text.substring(0, 2900), language })
      });
      if (!res.ok) {
        // Surface the failure so it's not a silent no-op (e.g. 401 after the
        // session expires, or a 5xx). The button just resets.
        console.error('Read aloud (TTS) failed:', res.status, res.statusText);
        setTtsPlayingId(null);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      setTtsPlayingId(id);
      audio.onended = () => { setTtsPlayingId(null); ttsAudioRef.current = null; };
      audio.onerror = () => { setTtsPlayingId(null); ttsAudioRef.current = null; };
      audio.play().catch(() => setTtsPlayingId(null));
    } catch (e) {
      console.error('Read aloud (TTS) error:', e);
      setTtsPlayingId(null);
    }
  };

  // The question statement can arrive under different keys depending on language
  // (e.g. `prompt` for most, `question_text` for Italian). Pick whichever is present.
  const questionText = (question) =>
    question?.prompt || question?.question_text || question?.question || question?.text || question?.statement || '';

  // Option display: prefix "A. " for real content, but leave generic placeholders
  // like "Option A" / "Opzione B" / "Option A." untouched (they already carry the letter).
  const optionDisplay = (letter, label) => {
    const text = String(label ?? '').trim();
    if (!text) return `${letter}.`;
    // Already starts with its letter (e.g. "A.", "A)", "A -") — don't double it up.
    if (new RegExp(`^${letter}\\s*[\\.\\)\\-:]`, 'i').test(text)) return text;
    // Generic "Option X" style placeholder in any language (Option/Opzione/Opción/...).
    if (/^(option|opzione|opci[oó]n|optionen|choix|antwort)\b/i.test(text)) return text;
    return `${letter}. ${text}`;
  };

  // Flatten an option (plain, website-preview, or schedule/detail) into speakable text.
  const optionToSpeech = (o, i) => {
    const letter = ['A', 'B', 'C', 'D'][i] || `${i + 1}`;
    const bits = [];
    if (o && typeof o === 'object') {
      const label = o.text || o.label || o.value || '';
      if (label) bits.push(label);
      const site = normalizeWebsiteOption(o);
      if (site) {
        if (site.title) bits.push(site.title);
        if (site.subtitle) bits.push(site.subtitle);
        if (site.url) bits.push(site.url);
        if (Array.isArray(site.sublinks) && site.sublinks.length) bits.push(site.sublinks.map(String).join(', '));
      } else {
        getOptionFields(o).forEach(f => {
          const v = typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value);
          bits.push(`${f.label}: ${v}`);
        });
      }
    } else {
      bits.push(String(o ?? ''));
    }
    return `${letter}. ${bits.filter(Boolean).join('. ')}`;
  };

  // Passage-level Read Aloud (icon in the passage box): section heading ->
  // task label/instruction -> passage title + body -> per-question notice ->
  // question statement. Does NOT read the answer options.
  const buildPassageReadText = (it, question, key) => {
    const parts = [];
    parts.push(`Section ${SECTION_LABELS[key] || ''}`.trim());
    const taskLabel = it?.passage?.task_label || it?.audio?.task_label || it?.task?.task_label;
    const taskInstruction = it?.passage?.task_instruction || it?.audio?.task_instruction
      || it?.task?.task_instruction || currentSection?.data?.global_instruction;
    if (taskLabel) parts.push(taskLabel);
    if (taskInstruction) parts.push(taskInstruction);
    const content = getPassageContent(it);
    if (content) {
      if (content.title) parts.push(content.title);
      if (content.body) parts.push(content.body);
    }
    if (question?.notice_title) parts.push(question.notice_title);
    if (question?.notice_body) parts.push(question.notice_body);
    if (question?.notice_source) parts.push(question.notice_source);
    const qText = questionText(question);
    if (qText) parts.push(qText);
    return parts.filter(p => p && String(p).trim()).join('. ');
  };

  // Question-level Read Aloud (icon below the options): reads only the question
  // statement and its answer options.
  const buildQuestionReadText = (question) => {
    const parts = [];
    const qText = questionText(question);
    if (qText) parts.push(qText);
    const opts = (question?.options || []).map((o, i) => optionToSpeech(o, i));
    if (opts.length) parts.push(opts.join('. '));
    return parts.filter(p => p && String(p).trim()).join('. ');
  };

  // Writing/Speaking task Read Aloud: label + instruction + any content points,
  // chips/topics, and scenario prompt so the learner hears the full task.
  const buildTaskReadText = (task) => {
    if (!task) return '';
    const parts = [];
    if (task.task_label) parts.push(task.task_label);
    if (task.task_instruction) parts.push(task.task_instruction);
    // Writing content points
    if (Array.isArray(task.content_points)) parts.push(...task.content_points);
    // Interview examiner questions
    (Array.isArray(task.examiner_questions) ? task.examiner_questions : []).forEach(qn => parts.push(qn));
    // Speaking chips (Interview / Exchange-of-information — display or selectable)
    const chips = Array.isArray(task.chips) ? task.chips : [];
    chips.forEach(c => parts.push(typeof c === 'string' ? c : (c.title || c.label || '')));

    // Flatten a topic/scenario the user can choose from, reading its FULL content
    // (label, title, detail/document/situation, and keywords) — same as the card.
    const readChoice = (item, idx, labelWord) => {
      if (!item) return;
      if (typeof item === 'string') { parts.push(item); return; }
      if (item.topic_label) parts.push(item.topic_label);
      else if (labelWord) parts.push(`${labelWord} ${idx + 1}`);
      if (item.title) parts.push(item.title);
      // Exposé document meta
      if (item.document_type || item.document_source) {
        parts.push([item.document_type, item.document_source].filter(Boolean).join(' '));
      }
      // Main body text (matches the card's detailText resolution)
      const sp = item.structured_prompts || item.structuredPrompts;
      let structured = '';
      if (typeof sp === 'string') structured = sp;
      else if (Array.isArray(sp)) structured = sp.map(x => (typeof x === 'string' ? x : (x?.description || x?.text || x?.prompt || x?.value || ''))).filter(Boolean).join('. ');
      else if (sp && typeof sp === 'object') structured = sp.description || sp.text || sp.prompt || '';
      const detail = item.question_detail || structured || item.document_declencheur || item.document_text || item.situation_description || item.situation || item.details || '';
      if (detail) parts.push(detail);
      // Keyword hints (Monologue theme cards)
      if (Array.isArray(item.keywords) && item.keywords.length) parts.push(item.keywords.join(', '));
    };

    // Monologue / Exposé topics (chooseable before speaking)
    (Array.isArray(task.topics) ? task.topics : []).forEach((t, i) => readChoice(t, i, 'SUJET'));
    // Dialogue scenarios (chooseable before the conversation)
    (Array.isArray(task.scenarios) ? task.scenarios : []).forEach((s, i) => readChoice(s, i, 'SUJET'));

    return parts.filter(p => p && String(p).trim()).join('. ');
  };

  // Play/pause a completed recording
  const togglePlayback = (url) => {
    if (isPlayingBack && playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
      setIsPlayingBack(false);
      return;
    }
    const a = new Audio(url);
    playbackAudioRef.current = a;
    a.ontimeupdate = () => { if (a.duration) setPlaybackProgress((a.currentTime / a.duration) * 100); };
    a.onended = () => { setIsPlayingBack(false); setPlaybackProgress(100); playbackAudioRef.current = null; };
    a.play().catch(() => setIsPlayingBack(false));
    setIsPlayingBack(true);
  };

  // Toggle a topic for AskAnswerQuestions (max 2)
  const toggleTopic = (taskId, topicId, maxSelect = 2) => {
    setSelectedTopics(prev => {
      const cur = prev[taskId] || [];
      if (cur.includes(topicId)) {
        return { ...prev, [taskId]: cur.filter(t => t !== topicId) };
      }
      if (cur.length >= maxSelect) return prev; // limit reached
      return { ...prev, [taskId]: [...cur, topicId] };
    });
  };

  const extractReply = (data) =>
    data?.content?.response || data?.response
    || data?.content?.agent_response || data?.agent_response
    || data?.content?.message || data?.message
    || data?.content?.reply || data?.reply || '';

  // Speak the avatar's reply via TTS (same as Business Scenario)
  const speakDialogueReply = async (text) => {
    if (!text) return;
    dlgLastReplyRef.current = text;
    stopAvatarSpeech();
    setDlgAvatarMuted(false);
    setDlgSpeaking(true);
    const voiceMap = { de: 'Hans', fr: 'Mathieu', es: 'Enrique', it: 'Giorgio', ja: 'Takumi', en: 'Matthew' };
    const voice = voiceMap[language] || 'Hans';
    try { await speakWithAvatar(text, null, voice); } catch (e) { console.error('Dialogue TTS error:', e); }
    setDlgSpeaking(false);
  };

  // ---- Speaking dialogue (avatar_dialogue) flow ----
  const startDialogue = async (task, scenarioId) => {
    // Starting the conversation ends preparation time and resumes the main exam timer.
    stopPrep(task.task_id);
    setDialogueBusy(true);
    // Capture the WHOLE conversation from the mic into a MediaRecorder so we can
    // send the full audio as base64 to the evaluate agent on Save & Next. This is
    // a dedicated recording stream, separate from the STT mic, so it doesn't
    // interfere with speech-to-text.
    dialogueChunksRef.current = [];
    dialogueAudioRef.current[task.task_id] = undefined;
    try {
      const recStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      dialogueStreamRef.current = recStream;
      const mr = new MediaRecorder(recStream, { mimeType: 'audio/webm' });
      dialogueRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) dialogueChunksRef.current.push(e.data); };
      dialogueStartTsRef.current[task.task_id] = Date.now();
      mr.start(1000); // gather chunks every second across the whole conversation
    } catch (recErr) {
      console.warn('Dialogue recording unavailable:', recErr?.message);
      dialogueRecorderRef.current = null;
    }
    try {
      const res = await axios.post(`${API_BASE}/exam-prep/speaking/dialogue/start`, {
        session_id: examSessionId,
        task_id: task.task_id,
        scenario_id: scenarioId
      }, { withCredentials: true });
      const startData = await readEpResponse(res.data);
      const reply = extractReply(startData);
      const initialTurns = reply ? [{ role: 'avatar', text: reply }] : [];
      dialogueTurnsRef.current[task.task_id] = initialTurns;
      setDialogueState(prev => ({
        ...prev,
        [task.task_id]: { started: true, scenarioId, ended: false, turns: initialTurns }
      }));
      // Conversation max always comes from the agent's dialogue_duration_sec[1].
      const maxSec = getDialogueMaxSec(task);
      setDialogueElapsed(0);
      if (dialogueTimerRef.current) clearInterval(dialogueTimerRef.current);
      dialogueTimerRef.current = setInterval(() => {
        setDialogueElapsed(prev => {
          if (prev + 1 >= maxSec) {
            clearInterval(dialogueTimerRef.current);
            dialogueTimerRef.current = null;
            // Auto-stop the conversation at the max duration
            endDialogue(task);
            return maxSec;
          }
          return prev + 1;
        });
      }, 1000);
      if (reply) speakDialogueReply(reply);
    } catch (e) {
      console.error('Dialogue start error:', e);
    }
    setDialogueBusy(false);
  };

  // BS-style mic toggle for dialogue avatar
  const toggleDlgMic = (task) => {
    if (dlgMicActive) {
      // Capture the displayed text into the ref before stopping
      // (in case only partial results arrived and the ref has nothing)
      if (!dialogueTranscriptRef.current && dialogueInputRef.current) {
        dialogueTranscriptRef.current = dialogueInputRef.current.trim();
      }
      // stop listening and send
      setDlgMicActive(false);
      stopDialogueListeningAndSend(task);
    } else {
      // stop avatar speech when user starts talking
      if (getCurrentAudio()) { getCurrentAudio().pause(); getCurrentAudio().currentTime = 0; }
      stopAvatarSpeech();
      setDlgSpeaking(false);
      setDlgAvatarMuted(true);
      setDlgMicActive(true);
      startDialogueListening(task);
    }
  };

  const toggleDlgSpeaker = (task) => {
    const last = dlgLastReplyRef.current;
    if (!last) return;
    if (!dlgAvatarMuted) {
      if (getCurrentAudio()) getCurrentAudio().pause();
      stopAvatarSpeech();
      setDlgAvatarMuted(true);
      setDlgSpeaking(false);
    } else {
      setDlgAvatarMuted(false);
      speakDialogueReply(last);
    }
  };

  const toggleDlgCamera = async () => {
    if (dlgCameraOn) {
      if (dlgCamStreamRef.current) { dlgCamStreamRef.current.getTracks().forEach(t => t.stop()); dlgCamStreamRef.current = null; }
      setDlgCameraOn(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        dlgCamStreamRef.current = stream;
        setDlgCameraOn(true);
        setTimeout(() => { if (dlgVideoRef.current) dlgVideoRef.current.srcObject = stream; }, 100);
      } catch (e) { console.error('Camera access denied:', e); }
    }
  };

  // Mic: start listening (STT streams speech to text) — matches the working version exactly
  const startDialogueListening = async (task) => {
    dialogueTranscriptRef.current = '';
    setDialogueListening(true);
    try {
      await startSTT('/stt', { language: STT_LANG[language] || 'de-DE' }, ({ text, isPartial }) => {
        if (isPartial) {
          setDialogueInput((dialogueTranscriptRef.current + ' ' + text).trim());
        } else {
          dialogueTranscriptRef.current = (dialogueTranscriptRef.current + ' ' + text).trim();
          setDialogueInput(dialogueTranscriptRef.current);
        }
      });
    } catch (e) {
      console.error('Dialogue STT error:', e);
      setDialogueListening(false);
    }
  };

  // Mic: stop listening and send the transcript as a turn
  const stopDialogueListeningAndSend = async (task) => {
    stopSTT();
    setDialogueListening(false);
    // give STT a moment to flush the final transcript
    setTimeout(() => sendDialogueTurn(task), 400);
  };

  const sendDialogueTurn = async (task) => {
    const text = (dialogueInput || dialogueTranscriptRef.current || '').trim();
    if (!text) return;
    setDialogueBusy(true);
    setDialogueInput('');
    dialogueTranscriptRef.current = '';
    // Add the user's turn (keep ref in sync)
    const afterUser = [...(dialogueTurnsRef.current[task.task_id] || []), { role: 'user', text }];
    dialogueTurnsRef.current[task.task_id] = afterUser;
    setDialogueState(prev => ({
      ...prev,
      [task.task_id]: { ...prev[task.task_id], turns: afterUser }
    }));
    try {
      const res = await axios.post(`${API_BASE}/exam-prep/speaking/dialogue/turn`, {
        session_id: examSessionId,
        task_id: task.task_id,
        user_text: text
      }, { withCredentials: true });
      const turnData = await readEpResponse(res.data);
      const reply = extractReply(turnData);
      if (reply) {
        const afterAvatar = [...(dialogueTurnsRef.current[task.task_id] || []), { role: 'avatar', text: reply }];
        dialogueTurnsRef.current[task.task_id] = afterAvatar;
        setDialogueState(prev => ({
          ...prev,
          [task.task_id]: { ...prev[task.task_id], turns: afterAvatar }
        }));
        speakDialogueReply(reply);
      }
    } catch (e) {
      console.error('Dialogue turn error:', e);
    }
    setDialogueBusy(false);
  };

  const endDialogue = async (task) => {
    setDialogueBusy(true);
    // Stop the conversation timer
    if (dialogueTimerRef.current) { clearInterval(dialogueTimerRef.current); dialogueTimerRef.current = null; }
    // Stop STT if still listening
    if (dialogueListening) { stopSTT(); setDialogueListening(false); }

    // Stop the conversation recorder and wait for its final chunk, then build the
    // full-conversation base64 that Save & Next sends to the evaluate agent.
    const recorder = dialogueRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      await new Promise((resolve) => {
        recorder.onstop = () => resolve();
        try { recorder.stop(); } catch { resolve(); }
      });
    }
    dialogueRecorderRef.current = null;
    // Release the recording mic stream now that we've captured everything
    if (dialogueStreamRef.current) { dialogueStreamRef.current.getTracks().forEach(t => t.stop()); dialogueStreamRef.current = null; }

    if (dialogueChunksRef.current.length > 0) {
      try {
        const blob = new Blob(dialogueChunksRef.current, { type: 'audio/webm' });
        dialogueAudioRef.current[task.task_id] = await blobToBase64(blob);
        // Store a playback entry so the ended-state UI can replay the full
        // conversation (waveform + play button + "Xm Ys Recorded").
        const startTs = dialogueStartTsRef.current[task.task_id];
        // Never exceed this task's own conversation cap (agent dialogue_duration_sec[1]),
        // even if the auto-stop fired a tick late.
        const taskMaxSec = getDialogueMaxSec(task);
        const rawSecs = startTs ? Math.max(1, Math.round((Date.now() - startTs) / 1000)) : 0;
        const secs = Math.min(rawSecs, taskMaxSec);
        setRecordings(prev => ({ ...prev, [task.task_id]: { blob, url: URL.createObjectURL(blob), time: secs } }));
      } catch (e) { console.error('Dialogue audio build error:', e); }
    }
    try {
      // End the dialogue on the agent (no evaluation here — that happens on Save & Next).
      // The agent end is a best-effort signal; failing it must NOT block ending locally.
      const endRes = await axios.post(`${API_BASE}/exam-prep/speaking/dialogue/end`, {
        session_id: examSessionId,
        task_id: task.task_id
      }, { withCredentials: true });
      if (endRes.data && endRes.data.success === false) {
        console.warn('Dialogue end (agent) reported an issue:', endRes.data.error);
      }
    } catch (e) {
      console.error('Dialogue end request failed (continuing locally):', e?.response?.status, e?.message);
    }
    // Mark ended so the UI shows "Recording completed" and the mic controls disappear
    setDialogueState(prev => ({
      ...prev,
      [task.task_id]: { ...prev[task.task_id], ended: true }
    }));
    setDialogueBusy(false);
  };

  // Reading/listening scoring is done SERVER-SIDE (correct answers never reach
  // the browser). The objective score is fetched from /exam-prep/score at submit.

  // Evaluate writing tasks via agent. Returns { results, records }
  const evaluateWriting = async () => {
    const results = {};
    const records = [];
    const writingSec = sections.find(s => s.key === 'writing');
    if (!writingSec || writingSec.data.unavailable) return { results, records };
    for (const task of (writingSec.data.tasks || [])) {
      const userAnswer = answers[task.task_id];
      const skipped = !userAnswer || userAnswer === '__skipped__';
      // Already evaluated on Save-and-Next? reuse it, don't re-hit the agent
      if (writingResults[task.task_id]) {
        results[task.task_id] = writingResults[task.task_id];
        continue;
      }
      let evaluation = null;
      // Evaluate even skipped tasks: the agent returns the model answer/corrections
      // (in `corrections[].corrected`), which we show as the correct answer.
      try {
        const res = await axios.post(`${API_BASE}/exam-prep/writing/evaluate`, {
          session_id: examSessionId,
          task_id: task.task_id,
          user_answer: skipped ? '' : userAnswer
        }, { withCredentials: true });
        if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; results[task.task_id] = evaluation; }
      } catch (e) { console.error('Writing eval error:', e); }
      records.push({
        section: 'writing',
        item_id: task.task_id,
        item_number: task.task_num,
        question_text: task.task_instruction || task.task_label,
        user_answer: skipped ? '__skipped__' : (typeof userAnswer === 'object' ? JSON.stringify(userAnswer) : userAnswer),
        is_skipped: skipped,
        evaluation,
        score: skipped ? 0 : getEvalScore(evaluation),
        max_score: task.marks != null ? task.marks : null
      });
    }
    return { results, records };
  };

  // Evaluate speaking tasks via agent (single-recording tasks only; dialogue handled separately)
  const evaluateSpeaking = async () => {
    const results = {};
    const records = [];
    const speakingSec = sections.find(s => s.key === 'speaking');
    if (!speakingSec || speakingSec.data.unavailable) return { results, records };
    for (const task of (speakingSec.data.tasks || [])) {
      if (task.mode === 'avatar_dialogue' || task.task_type === 'Dialogue') {
        // Dialogue evaluated via its own end flow — reuse cached result
        if (speakingResults[task.task_id]) results[task.task_id] = speakingResults[task.task_id];
        continue;
      }
      // Already evaluated on Save-and-Next? reuse it
      if (speakingResults[task.task_id]) { results[task.task_id] = speakingResults[task.task_id]; continue; }
      // The user's explicit choice wins: if this task is marked '__skipped__', treat
      // it as skipped at submit time even though a recording blob may still linger
      // in memory (we keep it so the user COULD return and submit it — but they
      // didn't). Send audio_base64: null so the agent returns model-answer feedback,
      // and record it as skipped (score 0).
      const isSkipped = answers[task.task_id] === '__skipped__';
      const rec = !isSkipped ? recordings[task.task_id] : null;
      let evaluation = null;
      // Evaluate even skipped tasks (null audio) so the agent returns the model
      // answer / feedback, shown as the correct answer — same as writing.
      try {
        const payload = { session_id: examSessionId, task_id: task.task_id, audio_base64: null };
        if (rec) payload.audio_base64 = await blobToBase64(rec.blob);
        const res = await axios.post(`${API_BASE}/exam-prep/speaking/evaluate`, payload, { withCredentials: true });
        if (res.data.success) { const d = await readEpResponse(res.data); evaluation = d?.content || d; results[task.task_id] = evaluation; }
      } catch (e) { console.error('Speaking eval error:', e); }
      records.push({
        section: 'speaking',
        item_id: task.task_id,
        item_number: task.task_num,
        question_text: task.task_instruction || task.task_label,
        user_answer: rec ? 'recorded' : '__skipped__',
        is_skipped: !rec,
        evaluation,
        score: rec ? getEvalScore(evaluation) : 0,
        max_score: task.marks != null ? task.marks : null
      });
    }
    return { results, records };
  };

  const finalizeAndShowResults = async () => {
    // A freshly completed exam is NOT a dashboard summary — hide the Back button.
    setFromDashboard(false);
    setSubmitting(true);
    // Reading/Listening were graded server-side on save. Fetch the objective
    // score from the server (correct answers never reach the browser).
    let objective = { correct: 0, total: 0, perSection: { reading: 0, listening: 0 }, records: [] };
    try {
      const scoreRes = await axios.get(`${API_BASE}/exam-prep/score?attempt_id=${attemptId}&_t=${Date.now()}`, { withCredentials: true });
      if (scoreRes.data && scoreRes.data.success) {
        const ps = scoreRes.data.perSection || { reading: 0, listening: 0 };
        const tot = scoreRes.data.totals || { reading: 0, listening: 0 };
        objective = {
          correct: (ps.reading || 0) + (ps.listening || 0),
          total: (tot.reading || 0) + (tot.listening || 0),
          perSection: { reading: ps.reading || 0, listening: ps.listening || 0 },
          records: []
        };
      }
    } catch (e) { console.error('Fetch score error:', e); }
    // Writing already evaluated on Save-and-Next (cached in writingResults); evaluate any missing ones
    const writing = await evaluateWriting();
    const mergedWriting = { ...writing.results, ...writingResults };
    // Speaking evaluated on Save-and-Next / dialogue end (cached); evaluate any missing
    const speaking = await evaluateSpeaking();
    const mergedSpeaking = { ...speaking.results, ...speakingResults };
    setWritingResults(mergedWriting);
    setSpeakingResults(mergedSpeaking);

    // Aggregate writing/speaking marks per-task from the ACTUAL attempt state.
    // A task counts only if it was genuinely attempted (writing: a non-skipped
    // answer; speaking: a recording exists / dialogue ended). Skipped tasks score
    // 0 even though their evaluation object may hold a model-answer score.
    const scoreWriting = () => {
      const sec = sections.find(s => s.key === 'writing');
      if (!sec || sec.data.unavailable) return 0;
      return (sec.data.tasks || []).reduce((sum, t) => {
        const ans = answers[t.task_id];
        const attempted = ans && ans !== '__skipped__';
        if (!attempted) return sum;
        return sum + (getEvalScore(mergedWriting[t.task_id]) || 0);
      }, 0);
    };
    const scoreSpeaking = () => {
      const sec = sections.find(s => s.key === 'speaking');
      if (!sec || sec.data.unavailable) return 0;
      return (sec.data.tasks || []).reduce((sum, t) => {
        const attempted = !!recordings[t.task_id] || dialogueState[t.task_id]?.ended;
        if (!attempted || answers[t.task_id] === '__skipped__') return sum;
        return sum + (getEvalScore(mergedSpeaking[t.task_id]) || 0);
      }, 0);
    };
    const writingScore = scoreWriting();
    const speakingScore = scoreSpeaking();

    // Use the frozen time on timeout; otherwise the elapsed time (duration - remaining).
    const timeTaken = timeTakenRef.current != null
      ? timeTakenRef.current
      : ((activeExamDurationRef.current || EXAM_DURATION) - timeLeft);
    const totalScore = objective.correct + writingScore + speakingScore;

    // Persist any records not already saved (writing + speaking evaluations, and
    // reading/listening as a safety upsert). Writing records carry the agent's
    // evaluation (feedback + corrections) which the result review displays.
    const allRecords = [...objective.records, ...writing.records, ...speaking.records];
    if (allRecords.length > 0) {
      try {
        await axios.post(`${API_BASE}/exam-prep/save-answers`, {
          attempt_id: attemptId,
          session_id: examSessionId,
          answers: allRecords
        }, { withCredentials: true });
      } catch (e) { console.error('Save answers error:', e); }
    }

    // Persist attempt completion with section scores
    try {
      await axios.post(`${API_BASE}/exam-prep/complete`, {
        attempt_id: attemptId,
        score: totalScore,
        total_questions: objective.total,
        time_taken: timeTaken,
        reading_score: objective.perSection.reading,
        listening_score: objective.perSection.listening,
        writing_score: writingScore,
        speaking_score: speakingScore
      }, { withCredentials: true });
    } catch (e) { console.error('Complete error:', e); }

    // ---- Build skill_scores payload for the agent ----
    // current: this attempt's per-skill score scaled to marks-per-skill (max, e.g. 25)
    // max: the config's marks-per-skill
    // previous: the user's last completed attempt's per-skill score (0 if none)
    const perSkillMax = Number(examConfig.marks_per_skill) || 25;
    const readingQs = getSectionItems(sections.find(s => s.key === 'reading') || {}).length;
    const listeningQs = getSectionItems(sections.find(s => s.key === 'listening') || {}).length;
    const scaleToSkill = (correct, totalQs) => (totalQs > 0 ? Math.round((correct / totalQs) * perSkillMax) : 0);
    const currentSkills = {
      listening: scaleToSkill(objective.perSection.listening, listeningQs),
      reading: scaleToSkill(objective.perSection.reading, readingQs),
      writing: Math.min(Math.round(writingScore), perSkillMax),
      speaking: Math.min(Math.round(speakingScore), perSkillMax)
    };

    // Fetch previous attempt's per-skill scores (last completed attempt for this language+level)
    let prevSkills = { listening: 0, reading: 0, writing: 0, speaking: 0 };
    try {
      const histRes = await axios.get(`${API_BASE}/exam-prep/history?language_code=${language}&level=${mapLevel(proficiencyLevel)}&_t=${Date.now()}`, { withCredentials: true });
      const hist = (histRes.data && histRes.data.history) || [];
      // Most recent COMPLETED attempt that isn't the one we just finished
      const prev = hist.find(h => h.status === 'completed' && String(h.id) !== String(attemptId));
      if (prev) {
        prevSkills = {
          listening: Number(prev.listening_score) || 0,
          reading: Number(prev.reading_score) || 0,
          writing: Number(prev.writing_score) || 0,
          speaking: Number(prev.speaking_score) || 0
        };
      }
    } catch (e) { /* no previous history — keep zeros */ }

    const skillScores = {
      listening: { previous: prevSkills.listening, current: currentSkills.listening, max: perSkillMax },
      reading:   { previous: prevSkills.reading,   current: currentSkills.reading,   max: perSkillMax },
      writing:   { previous: prevSkills.writing,   current: currentSkills.writing,   max: perSkillMax },
      speaking:  { previous: prevSkills.speaking,  current: currentSkills.speaking,  max: perSkillMax }
    };

    // Submit to the agent for final scoring + feedback
    let agentResult = null;
    try {
      const submitRes = await axios.post(`${API_BASE}/exam-prep/submit`, {
        session_id: examSessionId,
        language: mapLanguage(language),
        level: mapLevel(proficiencyLevel),
        duration_seconds: timeTaken,
        skill_scores: skillScores
      }, { withCredentials: true });
      if (submitRes.data.success) { const d = await readEpResponse(submitRes.data); agentResult = d?.content || d; }
    } catch (e) { console.error('Submit exam error:', e); }

    // Persist the agent FEEDBACK result so the summary (opened later from the list
    // page) can show the full headline/progress/keep-going text. It's stored as a
    // tagged exam_prep_exams row; getAttempt returns the latest feedback row.
    if (agentResult) {
      try {
        await axios.post(`${API_BASE}/exam-prep/save-feedback`, {
          attempt_id: attemptId,
          session_id: examSessionId,
          language_code: language,
          level: mapLevel(proficiencyLevel),
          agent_feedback: agentResult,
        }, { withCredentials: true });
      } catch (e) { console.error('Save feedback error (non-blocking):', e.message); }
    }

    // Fetch the saved per-question answers from OUR DB to build the
    // "Learn from Every Question" review (incorrect + skipped) from our data.
    let dbAnswers = [];
    try {
      const attRes = await axios.get(`${API_BASE}/exam-prep/attempt/${attemptId}?_t=${Date.now()}`, { withCredentials: true });
      if (attRes.data && attRes.data.success) dbAnswers = attRes.data.answers || [];
    } catch (e) { console.error('Fetch attempt answers error:', e); }

    const resultData = {
      objective,
      writingScore,
      speakingScore,
      writingResults: mergedWriting,
      speakingResults: mergedSpeaking,
      totalScore,
      timeTaken,
      agent: agentResult,
      dbAnswers
    };
    setResults(resultData);
    // Persist so a browser refresh keeps the user on the result page (with the
    // saved sections/exam context) instead of bouncing back to the intro.
    try {
      sessionStorage.setItem('ep_result', JSON.stringify({
        results: resultData,
        sections,
        answers,
        recordings: {}, // blobs can't be serialized; not needed for the result view
        dialogueTurns: dialogueTurnsRef.current,
        language, proficiencyLevel, attemptId, examConfig
      }));
    } catch (e) { /* storage may be unavailable; non-critical */ }
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitting(false);
    setView('results');
  };

  const handleSubmit = async () => {
    setShowSubmitPopup(false);
    await finalizeAndShowResults();
  };

  // Timer expired: stop everything, capture the time taken so the result shows the
  // real duration, then show a "Time's Up!" popup. The result is generated only
  // after the user clicks the button in the popup (confirmTimeout).
  const handleAutoSubmit = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    // Freeze the elapsed time now (timeLeft is 0 at expiry, so use the full duration).
    timeTakenRef.current = activeExamDurationRef.current || EXAM_DURATION;
    // Stop any media so nothing keeps recording/playing behind the popup.
    stopAudio();
    stopTts();
    try { stopSTT(); } catch (e) { /* noop */ }
    try { stopAvatarSpeech(); } catch (e) { /* noop */ }
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') { try { mediaRecorderRef.current.stop(); } catch (e) {} }
    if (dialogueRecorderRef.current && dialogueRecorderRef.current.state !== 'inactive') { try { dialogueRecorderRef.current.stop(); } catch (e) {} }
    setShowTimeoutPopup(true);
  };

  // User acknowledged the timeout — now generate the result for whatever was done.
  const confirmTimeout = async () => {
    setShowTimeoutPopup(false);
    await finalizeAndShowResults();
  };

  const handleExit = () => setShowExitPopup(true);
  const confirmExit = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    stopAudio();
    stopTts();
    try { stopSTT(); } catch (e) { /* noop */ }
    try { stopAvatarSpeech(); } catch (e) { /* noop */ }
    if (dlgCamStreamRef.current) { dlgCamStreamRef.current.getTracks().forEach(t => t.stop()); dlgCamStreamRef.current = null; }
    if (dialogueRecorderRef.current && dialogueRecorderRef.current.state !== 'inactive') { try { dialogueRecorderRef.current.stop(); } catch (e) { /* noop */ } }
    dialogueRecorderRef.current = null;
    if (dialogueStreamRef.current) { dialogueStreamRef.current.getTracks().forEach(t => t.stop()); dialogueStreamRef.current = null; }
    setShowExitPopup(false);
    navigate('/explore-learning', { state: { language, proficiencyLevel } });
  };
  const confirmLeave = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    stopAudio();
    stopTts();
    try { stopSTT(); } catch (e) { /* noop */ }
    try { stopAvatarSpeech(); } catch (e) { /* noop */ }
    if (dlgCamStreamRef.current) { dlgCamStreamRef.current.getTracks().forEach(t => t.stop()); dlgCamStreamRef.current = null; }
    if (dialogueRecorderRef.current && dialogueRecorderRef.current.state !== 'inactive') { try { dialogueRecorderRef.current.stop(); } catch (e) { /* noop */ } }
    dialogueRecorderRef.current = null;
    if (dialogueStreamRef.current) { dialogueStreamRef.current.getTracks().forEach(t => t.stop()); dialogueStreamRef.current = null; }
    if (dialogueTimerRef.current) { clearInterval(dialogueTimerRef.current); dialogueTimerRef.current = null; }
    setShowLeaveConfirm(false);
    navigate(pendingNavRef.current || '/');
    pendingNavRef.current = null;
  };
  const cancelLeave = () => { setShowLeaveConfirm(false); pendingNavRef.current = null; };

  // Answered count across objective sections
  const answeredCount = Object.keys(answers).filter(k => answers[k] && answers[k] !== '__skipped__').length;

  // Overall stats for the submit popup (all sections/items)
  const computeExamStats = () => {
    let total = 0, answered = 0, skipped = 0;
    sections.forEach(sec => {
      getSectionItems(sec).forEach(it => {
        total += 1;
        const key = it.question?.q_id || it.task?.task_id;
        const val = answers[key];
        const recorded = it.task && recordings[it.task.task_id];
        const dialogueDone = it.task && dialogueTurnsRef.current[it.task.task_id]?.length > 0;
        if (val === '__skipped__') skipped += 1;
        else if ((val && val !== '__skipped__') || recorded || dialogueDone) answered += 1;
      });
    });
    const attemptedPct = total > 0 ? Math.round((answered / total) * 100) : 0;
    return { total, answered, skipped, attemptedPct };
  };

  // ===================== LOADING =====================
  if (loading) {
    return (
      <div className="exam-prep-page">
        <Header user={user} />
        <div className="nav-header-container">
          <SideNav />
          <PageHeader breadcrumbs={breadcrumbs} language={language} proficiencyLevel={proficiencyLevel} />
        </div>
        <main className="exam-prep-content"><p>Loading...</p></main>
        <Footer />
      </div>
    );
  }

  // ===================== INTRO =====================
  if (view === 'intro') {
    return (
      <div className="exam-prep-page ep-intro-page">
        <Header user={user} />
        <SideNav />
        <main className="ep-intro-main">
          {/* Top block (white) */}
          <div className="ep-intro-top">
            <h1 className="ep-intro-heading">
              Ready for Your <span className="ep-intro-heading-purple">{langName} {mapLevel(proficiencyLevel)} Exam</span> Prep?
            </h1>
            <p className="ep-intro-desc">
              This practice exam is designed to simulate the format and timing of the official exam. Find a quiet place, ensure you have a stable internet connection, and begin only when you're ready.
            </p>
            <p className="ep-intro-sequence">
              Sequence will be: <span>Reading &rarr; Listening &rarr; Writing &rarr; Speaking.</span>
            </p>
          </div>

          {/* Middle band (#FAFAFF, full width) — top & bottom lines run edge to edge */}
          <div className="ep-intro-band">
            {/* Info chips sit on the top line */}
            <div className="ep-intro-chips">
              <div className="ep-intro-chip"><img src="/images/help.svg" alt="" width="16" height="16" /><span>{examConfig.total_questions} questions</span></div>
              <div className="ep-intro-chip"><img src="/images/Time-black.svg" alt="" width="16" height="16" /><span>{formatDuration(examConfig.duration_minutes)}</span></div>
              <div className="ep-intro-chip"><img src="/images/icon-assignment.svg" alt="" width="16" height="16" /><span>{examConfig.total_marks} marks ({examConfig.marks_per_skill} each Skill)</span></div>
              <div className="ep-intro-chip"><img src="/images/passingmarksicon.svg" alt="" width="16" height="16" /><span>Passing Marks {examConfig.passing_marks}/{examConfig.total_marks} ({Math.round((examConfig.passing_marks / examConfig.total_marks) * 100)}%)</span></div>
              <div className="ep-intro-chip"><img src="/images/Strategy.svg" alt="" width="16" height="16" /><span>2 attempts every 24 hours</span></div>
            </div>

            <div className="ep-intro-band-inner">
              {/* Two-column guidelines */}
              <div className="ep-intro-columns">
                <div className="ep-intro-col">
                  <h3 className="ep-intro-col-title">Exam Guidelines</h3>
                  <ul className="ep-intro-list">
                    <li>The exam follows the official structure for this level.</li>
                    <li>Read each question carefully before answering.</li>
                    <li>Some sections include audio that can only be played a limited number of times.</li>
                    <li>For <strong>{mapLevel(proficiencyLevel)}</strong>, you need minimum <strong>{examConfig.passing_marks}/{examConfig.total_marks}</strong> overall{examConfig.section_pass_min != null && <> and at least <strong>{examConfig.section_pass_min}/{examConfig.marks_per_skill}</strong> in each section</>}. Both criteria should be met.</li>
                    <li>Exiting the exam before submission will end your attempt, and your progress will not be saved.</li>
                  </ul>
                </div>
                <div className="ep-intro-col-divider"></div>
                <div className="ep-intro-col">
                  <h3 className="ep-intro-col-title">Things to Remember</h3>
                  <ul className="ep-intro-list">
                    <li>Your answers are submitted automatically when time ends, and the exam cannot be paused once started.</li>
                    <li>Complete the exam in one sitting and review your answers before submitting.</li>
                    <li>Use headphones for the best listening experience and keep your microphone accessible for speaking.</li>
                    <li>Ensure your device is fully charged or connected to a power source.</li>
                  </ul>
                </div>
              </div>

              {/* Selectors */}
              <div className="ep-intro-selectors">
                <div className="ep-intro-field">
                  <label className="ep-intro-field-label">Language</label>
                  <div className="ep-intro-lang-select">
                    <span className="ep-intro-lang-current">{langName} |</span>
                    <button className="ep-intro-lang-change" onClick={() => { setShowLangDropdown(!showLangDropdown); setShowLevelDropdown(false); }}>
                      Change Language
                      <img src="/images/purplearrow.svg" alt="" width="14" height="14" style={{ transform: showLangDropdown ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    {showLangDropdown && (
                      <div className="ep-intro-lang-dropdown">
                        {[...langList]
                          .sort((a, b) => {
                            // Japanese pinned to the top; then active-first ordering.
                            if (a.code === 'ja' && b.code !== 'ja') return -1;
                            if (b.code === 'ja' && a.code !== 'ja') return 1;
                            return a.active === b.active ? 0 : a.active ? -1 : 1;
                          })
                          .map(l => (
                          <button key={l.code} className={'ep-intro-lang-item' + (!l.active ? ' inactive' : '')}
                            disabled={!l.active}
                            onClick={() => {
                              if (!l.active) return;
                              setShowLangDropdown(false);
                              if (l.code === 'ja') {
                                // Japanese has its own dedicated Exam Prep page; switch to
                                // it (default level N5 — the user picks the level there).
                                navigate('/japanese-exam-prep', { state: { language: 'ja', proficiencyLevel: 'beginner-n5' } });
                                return;
                              }
                              setLanguage(l.code);
                            }}>
                            <img src={l.flag} alt="" width="20" height="14" />{l.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="ep-intro-field">
                  <label className="ep-intro-field-label">Proficiency Level</label>
                  <div className="ep-intro-level-select">
                    <button className="ep-intro-level-btn" onClick={() => { setShowLevelDropdown(!showLevelDropdown); setShowLangDropdown(false); }}>
                      <span className="ep-intro-level-left">
                        {langName} - {LEVEL_NAMES[mapLevel(proficiencyLevel)] || mapLevel(proficiencyLevel)}
                      </span>
                      <img src="/images/dropdownarrow.svg" alt="" width="18" height="18" style={{ transform: showLevelDropdown ? 'rotate(180deg)' : 'none' }} onError={e => { e.target.style.display = 'none'; }} />
                    </button>
                    {showLevelDropdown && (
                      <div className="ep-intro-level-dropdown">
                        {(language === 'ja' ? ['N5', 'N4', 'N3', 'N2', 'N1'] : ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']).map(lvl => {
                          // C1 and C2 are not yet available for Exam Prep
                          const disabled = lvl === 'C1' || lvl === 'C2';
                          return (
                            <button key={lvl}
                              className={'ep-intro-level-item' + (mapLevel(proficiencyLevel) === lvl ? ' selected' : '') + (disabled ? ' inactive' : '')}
                              disabled={disabled}
                              onClick={() => { if (!disabled) { setProficiencyLevel(lvl); setShowLevelDropdown(false); } }}>
                              {LEVEL_NAMES[lvl] || lvl}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="ep-intro-lang-hint">
                <img src="/images/black-info.svg" alt="" width="14" height="14" />
                <span>You have already selected {langName}, you can continue or change language here</span>
              </div>

              {/* Confirm checkbox */}
              <label className="ep-intro-checkbox">
                <input type="checkbox" checked={confirmChecked} onChange={e => setConfirmChecked(e.target.checked)} />
                <span>I understand my language selection cannot be changed once the challenge begins</span>
              </label>

              {genError && <div className="ep-gen-error">{genError}</div>}
            </div>
          </div>

          {/* Start button sits on the bottom line (below the band) */}
          <div className="ep-intro-start-wrap">
            <button className="ep-btn-start" onClick={handleStartExam} disabled={!canAttempt || !confirmChecked || generating}>
              {generating ? 'Preparing your exam...' : 'Start Exam Prep'}
              {!generating && <img src="/images/arrow-right.svg" alt="" width="20" height="20" />}
            </button>
            {!canAttempt && (
              <p className="ep-intro-no-attempts">You have used all attempts. Please try again after 24 hours.</p>
            )}
          </div>
        </main>
        <Footer />

        {/* Loading popup while the exam generates */}
        {generating && (
          <div className="bw-confirm-overlay">
            <div className="ep-gen-loading">
              <img src="/images/loadingicon.svg" alt="" className="ep-submitting-icon" />
              <h3 className="ep-gen-loading-title">Hold on a moment!</h3>
              <p className="ep-gen-loading-text">We&#8217;re getting your questions ready. Your exam is almost ready to begin.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===================== RESULTS =====================
  if (view === 'results' && results) {
    const { objective, writingScore, speakingScore, timeTaken, agent } = results;
    // Prefer our locally-measured time; only use the agent's value when it's a
    // positive number (it sometimes returns 0, which caused "0 minutes 0 seconds").
    const usedSeconds = (Number(timeTaken) > 0)
      ? timeTaken
      : (Number(agent?.completed_in_seconds) > 0 ? Number(agent.completed_in_seconds) : (Number(timeTaken) || 0));
    const timeMins = Math.floor(usedSeconds / 60);
    const timeSecs = usedSeconds % 60;
    const perSkillMax = Number(examConfig.marks_per_skill) || 25;

    // ---- Overall score (agent: overall.{score,out_of,percent,passed}) ----
    const overallScore = agent?.overall?.score != null ? Math.round(agent.overall.score)
      : objective.total > 0 ? Math.round((objective.correct / objective.total) * 100) : 0;
    const maxScore = agent?.overall?.out_of != null ? agent.overall.out_of : 100;
    const scorePct = agent?.overall?.percent != null ? Math.round(agent.overall.percent)
      : (maxScore > 0 ? Math.round((overallScore / maxScore) * 100) : overallScore);
    // `passed`/`statusLabel`/`headline` are computed AFTER sectionScores below, using
    // the per-language DB criteria (overall passing_marks + per-section section_pass_min),
    // so we don't rely on the agent's own pass/fail which ignores our criteria.
    const summary = agent?.summary || "Here's a summary of your performance.";
    const keepGoing = agent?.keep_going_message || null;
    // Highlights (agent.highlights.items[] = { title, message, type })
    const highlights = (agent?.highlights?.items) || [];
    const highlightsTitle = agent?.highlights?.section_title || 'Your progress';

    // ---- Section-wise scores (agent.section_scores[] = { section, marks, max_marks, percent }) ----
    const scaleToSkill = (correct, totalQs) => (totalQs > 0 ? Math.round((correct / totalQs) * perSkillMax) : 0);
    const readingQs = getSectionItems(sections.find(s => s.key === 'reading') || {}).length;
    const listeningQs = getSectionItems(sections.find(s => s.key === 'listening') || {}).length;
    const sectionScores = (Array.isArray(agent?.section_scores) && agent.section_scores.length)
      ? agent.section_scores.map(s => ({
          section: s.section,
          score: Number(s.marks) || 0,
          max: Number(s.max_marks) || perSkillMax,
          pct: s.percent != null ? Math.round(s.percent) : null
        }))
      : [
          { section: 'Reading', score: scaleToSkill(objective.perSection.reading, readingQs), max: perSkillMax, pct: null },
          { section: 'Listening', score: scaleToSkill(objective.perSection.listening, listeningQs), max: perSkillMax, pct: null },
          { section: 'Writing', score: Math.min(writingScore, perSkillMax), max: perSkillMax, pct: null },
          { section: 'Speaking', score: Math.min(speakingScore, perSkillMax), max: perSkillMax, pct: null }
        ];
    // Enforce the exam section order (Reading -> Listening -> Writing -> Speaking)
    // regardless of the order the agent returned section_scores in.
    const SECTION_RANK = { reading: 0, listening: 1, writing: 2, speaking: 3 };
    const rankOf = (name) => {
      const k = String(name || '').toLowerCase();
      return SECTION_RANK[k] != null ? SECTION_RANK[k] : 99;
    };
    sectionScores.sort((a, b) => rankOf(a.section) - rankOf(b.section));
    // Same order for the "Your progress" highlights — infer the section from the
    // highlight's title/section field (e.g. "Reading improved" -> reading).
    const highlightRank = (h) => {
      const text = String(h?.section || h?.title || '').toLowerCase();
      if (text.includes('read')) return 0;
      if (text.includes('listen')) return 1;
      if (text.includes('writ')) return 2;
      if (text.includes('speak')) return 3;
      return 99;
    };
    highlights.sort((a, b) => highlightRank(a) - highlightRank(b));

    // ---- Pass/fail from the per-language DB criteria (NOT the agent's own flag) ----
    // Rule: overall marks >= passing_marks AND every section's marks >= section_pass_min.
    const passingMarks = Number(examConfig.passing_marks) || 0;
    const sectionMin = examConfig.section_pass_min != null ? Number(examConfig.section_pass_min) : null;
    const overallMet = overallScore >= passingMarks;
    const sectionsMet = sectionMin == null
      ? true
      : sectionScores.every(s => Number(s.score) >= sectionMin);
    const passed = overallMet && sectionsMet;
    const statusLabel = passed ? 'Passed' : 'Not passed';

    const headline = agent?.headline || (passed ? "Well done! You've completed your mock test." : 'You have completed your mock test.');

    // ---- Learn from every question (built entirely from OUR DB answers) ----
    // dbAnswers rows: { section, item_id, item_number, question_text, options,
    // correct_answer, user_answer, is_correct, is_skipped, explanation }
    const dbAnswers = results.dbAnswers || [];
    const letterIndex = { A: 0, B: 1, C: 2, D: 3 };
    const optText = (opts, letterOrVal) => {
      if (letterOrVal == null || letterOrVal === '') return '';
      const arr = Array.isArray(opts) ? opts : [];
      const idx = letterIndex[letterOrVal];
      const label = (o) => (o && typeof o === 'object') ? (o.text || o.label || o.value || '') : o;
      if (idx != null && arr[idx] != null) return `${letterOrVal}. ${label(arr[idx])}`;
      return String(letterOrVal);
    };
    // Map a FormFilling writing task's field id -> label, so a stored answer like
    // {"f2":"Isabel",...} can be shown as readable "Label: value" lines.
    const formFieldLabels = (taskId) => {
      const wSec = sections.find(s => s.key === 'writing');
      const task = (wSec?.data?.tasks || []).find(t => t.task_id === taskId);
      const map = {};
      (task?.form?.fields || []).forEach(f => { if (f && f.id != null) map[f.id] = f.label || f.id; });
      return map;
    };
    const mapDbReview = (row) => {
      // Writing/Speaking rows carry an evaluation object (feedback + corrections)
      // instead of A/B/C/D options. Build a feedback-style review for them.
      if (row.section === 'writing' || row.section === 'speaking') {
        const ev = row.evaluation || {};
        // Treat empty / punctuation-only strings as no content (avoids a stray ".").
        const meaningful = (s) => {
          const t = String(s ?? '').trim();
          return /[\p{L}\p{N}]/u.test(t) ? t : '';
        };
        const corrections = Array.isArray(ev.corrections) ? ev.corrections : [];
        // The model/sample answer can arrive in several shapes depending on task
        // type and whether it was skipped:
        //   - expected_answer        -> speaking + writing (email/text, and skipped)
        //   - corrections[].corrected -> writing free-text (ShortMessage) attempts
        //   - field_results[].expected -> writing FormFilling (per-field expected)
        const fieldResults = Array.isArray(ev.field_results) ? ev.field_results : [];
        const sampleFromExpected = meaningful(ev.expected_answer);
        const sampleFromCorrections = meaningful(
          corrections.map(c => meaningful(c.corrected || c.correct_word)).filter(Boolean).join(' ')
        );
        // FormFilling: build "label: expected" lines so the correct answer is
        // readable per field (matches how the user's answer is shown).
        let correctFields = null;
        if (fieldResults.length > 0) {
          const labels = formFieldLabels(row.item_id);
          correctFields = fieldResults
            .filter(fr => fr && fr.field_id != null && meaningful(fr.expected))
            .map(fr => ({ label: labels[fr.field_id] || fr.field_id, value: String(fr.expected) }));
          if (!correctFields.length) correctFields = null;
        }
        const sample = sampleFromExpected || sampleFromCorrections;
        // FormFilling answers are stored as a JSON object ({"f2":"Isabel",...}).
        // Render them as readable field/value lines instead of raw JSON.
        let yourText = '';
        let yourAnswerFields = null;
        const raw = row.user_answer;
        if (raw && raw !== '__skipped__') {
          const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
          const trimmed = str.trim();
          if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            try {
              const obj = JSON.parse(trimmed);
              const labels = formFieldLabels(row.item_id);
              yourAnswerFields = Object.entries(obj)
                .filter(([, v]) => v != null && String(v).trim() !== '')
                .map(([k, v]) => ({ label: labels[k] || k, value: String(v) }));
              if (!yourAnswerFields.length) yourAnswerFields = null;
            } catch (e) { yourText = meaningful(str); }
          } else {
            yourText = meaningful(str);
          }
        }
        // FormFilling: if we couldn't parse the user's answer from user_answer
        // (e.g. it was stored as '__skipped__'), fall back to field_results[].got
        // so the result page still shows what the user entered per field.
        if (!yourAnswerFields && fieldResults.length > 0) {
          const labels = formFieldLabels(row.item_id);
          const gotFields = fieldResults
            .filter(fr => fr && fr.field_id != null && meaningful(fr.got))
            .map(fr => ({ label: labels[fr.field_id] || fr.field_id, value: String(fr.got) }));
          if (gotFields.length) yourAnswerFields = gotFields;
        }
        return {
          section: SECTION_LABELS[row.section] || row.section || '',
          isEval: true,
          isSkipped: row.is_skipped === true,
          questionNum: row.item_number,
          yourAnswerFields: row.section === 'writing' ? yourAnswerFields : null,
          yourAnswerText: (row.section === 'writing' && yourText) ? yourText : null, // speaking answer is audio
          correctText: sample || null,
          correctFields: row.section === 'writing' ? correctFields : null,
          feedback: meaningful(ev.feedback) || '',
          corrections,
          explanation: ''
        };
      }
      return {
        section: SECTION_LABELS[row.section] || row.section || '',
        groupLabel: row.section === 'listening' ? 'Listening' : (row.section === 'reading' ? 'Reading' : ''),
        isSkipped: row.is_skipped === true,
        questionNum: row.item_number,
        yourAnswerText: (row.user_answer && row.user_answer !== '__skipped__') ? optText(row.options, row.user_answer) : null,
        correctText: optText(row.options, row.correct_answer),
        explanation: row.explanation || ''
      };
    };
    // Reading/Listening carry objective correctness; Writing/Speaking carry an
    // evaluation. A writing/speaking row is "incorrect" when it's attempted but
    // didn't earn full marks; "skipped" when is_skipped.
    const reviewRows = dbAnswers.filter(r => ['reading', 'listening', 'writing', 'speaking'].includes(r.section));
    const isEvalSection = (r) => r.section === 'writing' || r.section === 'speaking';
    const isIncorrect = (r) => r.is_skipped !== true && (
      isEvalSection(r)
        ? (Number(r.score) || 0) < (Number(r.max_score) || 0)   // not full marks
        : r.is_correct === false
    );
    const incorrectItems = reviewRows.filter(isIncorrect).map(mapDbReview);
    const skippedItems = reviewRows.filter(r => r.is_skipped === true).map(mapDbReview);
    const activeReview = resultReviewTab === 'skipped' ? skippedItems : incorrectItems;

    return (
      <div className="exam-prep-page ep-result-page">
        <Header user={user} />
        <SideNav />
        <main className="ep-result-main">
          <h1 className="ep-result-title"><span className="ep-result-title-purple">Exam Prep</span> Result - {langName} <img src={`/images/flag-${langName.toLowerCase()}.svg`} alt="" width="22" height="22" style={{ verticalAlign: 'middle', marginLeft: '6px' }} /></h1>

          {/* Progress result card */}
          <div className="ep-result-card">
            <div className="ep-result-card-top">
              <span className="ep-result-card-title"><img src="/images/Star-black.svg" alt="" width="18" height="18" /> Progress result <span className="ep-result-date-divider"></span> <span className="ep-result-date">{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span></span>
              <span className="ep-result-time"><img src="/images/Info.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} /> Completed in <strong>{timeMins} minutes {timeSecs} seconds</strong></span>
            </div>
            <div className="ep-result-body">
              <div className="ep-result-score-box">
                <div className="ep-result-score-circle">
                  <svg viewBox="0 0 120 120" className="ep-score-svg">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#E6E6E6" strokeWidth="8" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#7500C0" strokeWidth="8"
                      strokeDasharray={`${(scorePct / 100) * 327} 327`} strokeLinecap="round" transform="rotate(-90 60 60)" />
                  </svg>
                  <div className="ep-score-text"><span className="ep-score-value">{overallScore}</span><span className="ep-score-total">out of {maxScore}</span></div>
                </div>
                <div className="ep-result-score-line">
                  <span className="ep-result-score-label">Score <strong>{overallScore}/{maxScore}</strong></span>
                  <span className={'ep-result-badge ' + (passed ? 'pass' : 'fail')}>{statusLabel}</span>
                </div>
              </div>
              <div className="ep-result-progress">
                <p className="ep-result-headline">{headline}</p>
                <p className="ep-result-summary">{summary}</p>
                {Array.isArray(highlights) && highlights.length > 0 && (
                  <>
                    <p className="ep-result-notes-title">{highlightsTitle}</p>
                    <div className="ep-result-notes">
                      {highlights.map((n, i) => (
                        <div key={i} className="ep-result-note">
                          <img src={n.type === 'improved' ? '/images/uplevelgreen.svg' : '/images/purpleInfo.svg'} alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} />
                          <div><strong>{n.title}</strong>{n.message && <p>{n.message}</p>}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Keep going */}
          {keepGoing && (
            <div className="ep-result-keepgoing">
              <img src="/images/keepgoing.svg" alt="" width="18" height="18" onError={e => { e.target.style.display = 'none'; }} />
              <div><strong>Keep going!</strong><p>{keepGoing}</p></div>
            </div>
          )}

          {/* Section-wise scores */}
          <div className="ep-result-section-scores">
            <h3 className="ep-result-block-title"><img src="/images/section-wiseicon.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /> Section-wise Scores</h3>
            <p className="ep-result-block-sub">A closer look at your performance in each section</p>
            <div className="ep-result-score-table">
              <div className="ep-result-score-head"><span className="ep-result-score-sec">Section</span><span className="ep-result-score-perf">Performance</span><span className="ep-result-score-num">Score per section</span></div>
              {sectionScores.map((s, i) => {
                const pct = s.pct != null ? s.pct : (s.max > 0 ? Math.round((s.score / s.max) * 100) : 0);
                return (
                  <div key={i} className="ep-result-score-row">
                    <span className="ep-result-score-sec">{s.section}</span>
                    <span className="ep-result-score-perf">
                      <span className="ep-result-score-bar"><span className="ep-result-score-fill" style={{ width: pct + '%' }}></span></span>
                      <span className="ep-result-score-pct">{pct}%</span>
                    </span>
                    <span className="ep-result-score-num">{s.score}/{s.max}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Learn from every question */}
          <div className="ep-result-learn">
            <h3 className="ep-result-block-title"><img src="/images/purpleInfo.svg" alt="" width="16" height="16" onError={e => { e.target.style.display = 'none'; }} /> Learn from Every Question</h3>
            <p className="ep-result-block-sub">Go through each question to understand what you got right and where you can improve.</p>
            <div className="ep-result-tabs">
              <button className={'ep-result-tab' + (resultReviewTab === 'incorrect' ? ' active' : '')} onClick={() => setResultReviewTab('incorrect')}>Incorrect</button>
              <button className={'ep-result-tab' + (resultReviewTab === 'skipped' ? ' active' : '')} onClick={() => setResultReviewTab('skipped')}>Skipped</button>
            </div>
            <div className="ep-result-review-list">
              {activeReview.length === 0 ? (
                <p className="ep-result-empty">No {resultReviewTab} questions. Great job!</p>
              ) : (() => {
                // Group the review items by section (Listening, Reading, ...) so each
                // section shows once as a heading, like the Figma.
                const bySection = [];
                activeReview.forEach(r => {
                  let g = bySection.find(x => x.section === r.section);
                  if (!g) { g = { section: r.section, items: [] }; bySection.push(g); }
                  g.items.push(r);
                });
                // Fixed section order: Reading -> Listening -> Writing -> Speaking
                const sectionOrder = ['Reading', 'Listening', 'Writing', 'Speaking'];
                bySection.sort((a, b) => {
                  const ia = sectionOrder.indexOf(a.section); const ib = sectionOrder.indexOf(b.section);
                  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                });
                return bySection.map((g, gi) => (
                  <div key={gi} className="ep-result-review-group">
                    <h4 className="ep-result-review-section">{g.section}</h4>
                    {g.items.map((r, i) => (
                      <div key={i} className="ep-result-review-item">
                        {/* Result review shows ONLY the question number for every
                            section — never the question text/prompt. */}
                        <div className="ep-result-review-q">{r.questionNum != null ? `Question ${r.questionNum}` : ''}</div>
                        {/* FormFilling writing answer — readable field/value lines */}
                        {Array.isArray(r.yourAnswerFields) && r.yourAnswerFields.length > 0 && (
                          <div className="ep-result-review-form">
                            <span className="ep-result-form-heading">Your answer</span>
                            {r.yourAnswerFields.map((f, fi) => (
                              <div key={fi} className="ep-result-form-row">
                                <span className="ep-result-form-label">{f.label}:</span>
                                <span className="ep-result-form-value">{f.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Skipped: show "Not answered" with the skipped icon.
                            Incorrect: show the user's (wrong) selected answer with
                            the mistake icon. For writing FormFilling the fields block
                            above already shows the entered values, so only show the
                            "Not answered" line when there were no entered fields. */}
                        {r.isSkipped ? (
                          !(Array.isArray(r.yourAnswerFields) && r.yourAnswerFields.length > 0) && (
                            <div className="ep-result-review-ans skipped">
                              <img src="/images/skippedicon.svg" alt="" width="16" height="16" /> Not answered
                            </div>
                          )
                        ) : r.yourAnswerText && (
                          <div className="ep-result-review-ans wrong">
                            <img src="/images/mistakeicon.svg" alt="" width="16" height="16" /> {r.yourAnswerText}
                          </div>
                        )}
                        {/* FormFilling expected answer — readable field/value lines */}
                        {Array.isArray(r.correctFields) && r.correctFields.length > 0 && (
                          <div className="ep-result-review-form ep-result-review-form-correct">
                            <span className="ep-result-form-heading">Correct answer</span>
                            {r.correctFields.map((f, fi) => (
                              <div key={fi} className="ep-result-form-row">
                                <span className="ep-result-form-label">{f.label}:</span>
                                <span className="ep-result-form-value">{f.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {r.correctText && (
                          <div className="ep-result-review-ans right">
                            <img src="/images/Done.svg" alt="" width="18" height="18" /> {r.correctText}
                          </div>
                        )}
                        {/* Writing/Speaking feedback from the evaluation agent */}
                        {r.feedback && (
                          <div className="ep-result-review-exp">
                            <strong>Feedback</strong>
                            <p>{r.feedback}</p>
                          </div>
                        )}
                        {r.explanation && (
                          <div className="ep-result-review-exp">
                            <strong>Explanation</strong>
                            <p>{r.explanation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </div>

          <div className="ep-result-footer">
            <span className="ep-result-footer-hint"><img src="/images/purpleInfo.svg" alt="" width="14" height="14" /> {agent?.retake_note || 'Retake Exam Prep after learning and practicing to see your progress.'}</span>
            <button className="ep-btn-continue-learning" onClick={() => { try { sessionStorage.removeItem('ep_result'); } catch (e) {} navigate('/explore-learning', { state: { language, proficiencyLevel } }); }}>Continue Learning</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ===================== QUIZ =====================
  const sectionKey = currentSection?.key;
  const q = item?.question;

  // Listening pages (one page = one audio + all its questions). Used only for
  // the listening render/nav/qnav; scoring still uses the flattened sectionItems.
  const listeningPages = sectionKey === 'listening' ? getListeningPages(sectionItems) : [];
  const currentListeningPage = sectionKey === 'listening'
    ? listeningPages.find(p => currentItem >= p.startIndex && currentItem <= p.endIndex) || null
    : null;
  const currentListeningPageIndex = currentListeningPage ? listeningPages.indexOf(currentListeningPage) : -1;

  // Shared passage content for a question set. The agent may attach `content`
  // to the passage, the audio, or the individual question. Whatever is present
  // is shown as the passage for ALL questions in that set.
  const getPassageContent = (it) => {
    if (!it) return null;
    const c = it.passage?.content || it.audio?.content || it.question?.content;
    if (!c) return null;
    // content may be a plain string
    if (typeof c === 'string') return c.trim() ? { title: '', body: c } : null;
    if (typeof c !== 'object') return null;

    // Generic handling: the agent uses varied key names such as
    // { title, body }, { annonce_title, annonce_body, annonce_contact },
    // { email_subject, email_body }, etc. Pick a title from any *_title/title/
    // heading/subject key, and build the body from the remaining string fields
    // (in their original order), so any content shape renders as a passage.
    const entries = Object.entries(c).filter(([, v]) => v != null && String(v).trim() !== '');
    if (!entries.length) return null;

    const isTitleKey = (k) => /(^|_)(title|heading|subject)$/i.test(k);
    const titleEntry = entries.find(([k]) => isTitleKey(k));
    const title = titleEntry ? String(titleEntry[1]) : '';

    // Metadata keys that describe the passage but are NOT reading text — never
    // render these in the body (they'd otherwise dump raw JSON on screen).
    const isMetaKey = (k) => /^(meta|metadata|source|document_type|passage_type|task_label|task_instruction|id|passage_id|passage_num)$/i.test(k);

    const bodyParts = entries
      .filter(([k]) => !isTitleKey(k) && !isMetaKey(k))
      // Only string/number values are passage text. Skip nested objects/arrays so
      // structured metadata never leaks to the screen as JSON.
      .filter(([, v]) => typeof v !== 'object')
      .map(([, v]) => String(v));
    const body = bodyParts.join('\n\n');

    if (!title && !body) return null;
    return { title, body };
  };

  // Extra structured data carried on the options / question that isn't part of
  // the answer choice itself (e.g. duration, links, resources). Rendered in a
  // details box below the options.
  const getOptionDetails = (question) => {
    if (!question) return [];
    const details = [];
    // 1) option-level extras: when an option is an object with more than a label
    (question.options || []).forEach((opt, idx) => {
      if (opt && typeof opt === 'object') {
        const letter = ['A', 'B', 'C', 'D'][idx];
        Object.entries(opt).forEach(([k, v]) => {
          if (['text', 'label', 'value', 'option', 'id'].includes(k)) return;
          if (v == null || v === '') return;
          details.push({ label: `${letter} · ${k.replace(/_/g, ' ')}`, value: v });
        });
      }
    });
    // 2) question-level extras commonly named for durations / links / resources
    const extraKeys = ['duration', 'links', 'link', 'url', 'resources', 'reference', 'attachments', 'details', 'info'];
    extraKeys.forEach(k => {
      const v = question[k];
      if (v == null || v === '') return;
      if (Array.isArray(v)) v.forEach(item => details.push({ label: k, value: item }));
      else details.push({ label: k, value: v });
    });
    return details;
  };

  const isUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);

  // Renders the details box (duration, links, etc.) below the options.
  const renderOptionDetails = (question) => {
    const details = getOptionDetails(question);
    if (!details.length) return null;
    return (
      <div className="ep-option-details">
        {details.map((d, i) => (
          <div key={i} className="ep-option-detail-row">
            <span className="ep-option-detail-label">{d.label}:</span>
            {/* Links are shown as plain, non-clickable text (this is an exam, not a live page) */}
            {isUrl(d.value)
              ? <span className="ep-option-detail-link">{d.value}</span>
              : <span className="ep-option-detail-value">{typeof d.value === 'object' ? JSON.stringify(d.value) : String(d.value)}</span>}
          </div>
        ))}
      </div>
    );
  };

  // Normalize a "website preview" style option object into a common shape.
  // Handles keys like website_url/url, website_title/title, website_subtitle/subtitle,
  // and sublinks (array, or a stringified JSON array).
  const normalizeWebsiteOption = (opt) => {
    if (!opt || typeof opt !== 'object') return null;
    const get = (...keys) => {
      for (const k of keys) {
        const found = Object.keys(opt).find(ok => ok.toLowerCase() === k);
        if (found && opt[found] != null && opt[found] !== '') return opt[found];
      }
      return '';
    };
    const url = get('website_url', 'url', 'link');
    const title = get('website_title', 'title', 'name');
    const subtitle = get('website_subtitle', 'subtitle', 'description');
    let sublinks = get('sublinks', 'sub_links', 'links');
    if (typeof sublinks === 'string') {
      // may be a JSON array string like ["a","b"] or a pipe/comma separated list
      try {
        const parsed = JSON.parse(sublinks);
        sublinks = Array.isArray(parsed) ? parsed : [sublinks];
      } catch {
        sublinks = sublinks.split(/[|,]/).map(s => s.trim()).filter(Boolean);
      }
    }
    if (!Array.isArray(sublinks)) sublinks = sublinks ? [sublinks] : [];
    if (!url && !title && !subtitle && !sublinks.length) return null;
    return { url, title, subtitle, sublinks };
  };

  // Generic structured fields for an option object (anything beyond the plain
  // answer label). Used for schedule/detail-style options (e.g. Departure City,
  // Date, Departure Time). Returns [{ key, label, value }].
  const OPTION_LABEL_KEYS = ['text', 'label', 'value', 'option', 'id', 'is_correct', 'correct'];
  const getOptionFields = (opt) => {
    if (!opt || typeof opt !== 'object') return [];
    return Object.entries(opt)
      .filter(([k, v]) => !OPTION_LABEL_KEYS.includes(k.toLowerCase()) && v != null && v !== '')
      .map(([k, v]) => ({
        key: k,
        label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value: v
      }));
  };

  // A question uses "rich" option cards when at least one option is an object
  // carrying structured fields (website OR schedule/detail data).
  const hasRichOptions = (question) =>
    (question?.options || []).some(o =>
      normalizeWebsiteOption(o) !== null || getOptionFields(o).length > 0
    );

  // Renders option cards (radio + card). Website-style options render as a
  // preview card (URL/title/subtitle/sublinks); other structured options render
  // as a labeled fields card (Departure City, Date, ...), with links as links.
  const renderRichOptions = (question) => {
    const opts = question.options || [];
    return (
      <div className="ep-rich-options">
        {opts.map((opt, idx) => {
          const letter = ['A', 'B', 'C', 'D'][idx];
          const val = letter;
          const site = normalizeWebsiteOption(opt);
          const fields = site ? [] : getOptionFields(opt);
          const selected = answers[question.q_id] === val;
          return (
            <label key={idx} className={'ep-rich-option' + (selected ? ' selected' : '')}>
              <div className="ep-rich-option-head">
                <input type="radio" name={question.q_id} checked={selected} onChange={() => selectAnswer(question.q_id, val)} />
                <span className="bw-radio-custom"></span>
                <span className="ep-rich-option-label">Option {letter}</span>
              </div>

              {/* Website preview card */}
              {site && (
                <div className="ep-website-card">
                  <div className="ep-website-card-main">
                    {site.url && (
                      <div className="ep-website-url">
                        <img src="/images/globe.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} />
                        <span>{site.url}</span>
                      </div>
                    )}
                    {site.title && <div className="ep-website-title">{site.title}</div>}
                    {site.subtitle && <div className="ep-website-subtitle">{site.subtitle}</div>}
                  </div>
                  {site.sublinks.length > 0 && (
                    <div className="ep-website-sublinks">
                      {/* Sublinks are display-only (non-clickable) inside the exam */}
                      {site.sublinks.map((sl, si) => (
                        <span key={si} className="ep-website-sublink">
                          <img src="/images/link.svg" alt="" width="12" height="12" onError={e => { e.target.style.display = 'none'; }} />
                          {String(sl)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Generic structured fields card (schedule/details) */}
              {!site && fields.length > 0 && (
                <div className="ep-fields-card">
                  {fields.map((f, fi) => (
                    <div key={fi} className="ep-field-row">
                      <span className="ep-field-label">{f.label}:</span>
                      {/* Links shown as plain, non-clickable text */}
                      {isUrl(f.value)
                        ? <span className="ep-field-link">{f.value}</span>
                        : <span className="ep-field-value">{typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </label>
          );
        })}
      </div>
    );
  };

  // Renders the shared passage box (used by reading and listening).
  // `readInfo` (optional) = { question, sectionKey, id } enables a top-right
  // Read Aloud icon that reads only the section/instructions + passage + question.
  const renderPassageBox = (it, readInfo = null) => {
    const content = getPassageContent(it);
    if (!content) return null;
    const passageType = it.passage?.passage_type || '';
    const readId = readInfo ? `passage_${readInfo.id}` : null;
    return (
      <div className="ep-passage-box">
        <span className="ep-passage-icon">
          {/^(informal_email|formal_email|.*email.*)$/i.test(passageType) ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="5" width="18" height="14" rx="2" stroke="#555555" strokeWidth="1.5" />
              <path d="M3.5 6.5L12 13L20.5 6.5" stroke="#555555" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="#555555" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M14 3v5h5" stroke="#555555" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <div className="ep-passage-scroll">
          {content.title && <h3 className="ep-passage-title">{content.title}</h3>}
          {content.body && <div className="ep-passage-body">{content.body}</div>}
        </div>
        {readInfo && (
          <div className="ep-read-aloud ep-passage-read"
            onClick={() => handleReadAloud(readId, buildPassageReadText(it, readInfo.question, readInfo.sectionKey))}>
            <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
            <span>{ttsPlayingId === readId ? 'Stop' : 'Read aloud'}</span>
          </div>
        )}
      </div>
    );
  };

  // Per-question notice card (reading). Some reading questions carry their own
  // notice_title / notice_body / notice_source shown as a small card below the prompt.
  const renderNoticeCard = (question) => {
    if (!question) return null;
    const title = question.notice_title;
    const body = question.notice_body;
    const source = question.notice_source;
    if (!title && !body && !source) return null;
    // Unique id for the read-aloud toggle on this notice card.
    const noticeId = `notice_${question.q_id}`;
    const noticeReadText = [title, body, source].filter(Boolean).join('. ');
    return (
      <div className="ep-notice-card">
        <span className="ep-notice-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="#555555" strokeWidth="1.5" />
            <path d="M12 7v6" stroke="#555555" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1" fill="#555555" />
          </svg>
        </span>
        <div className="ep-notice-body-wrap">
          {title && <div className="ep-notice-title">{title}</div>}
          {body && <div className="ep-notice-body">{body}</div>}
          {source && <div className="ep-notice-source">{source}</div>}
        </div>
        {noticeReadText && (
          <div className="ep-read-aloud ep-notice-read"
            onClick={() => handleReadAloud(noticeId, noticeReadText)}>
            <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
            <span>{ttsPlayingId === noticeId ? 'Stop' : 'Read aloud'}</span>
          </div>
        )}
      </div>
    );
  };

  // Dialogue-in-progress: a dialogue task that was started but not yet ended.
  // Save & Next / Submit stay disabled until the user clicks End Conversation. Skip stays enabled.
  const isDialogueTask = sectionKey === 'speaking' && item?.task && (item.task.mode === 'avatar_dialogue' || item.task.task_type === 'Dialogue');

  // Speaking sub-title (task type) shown next to "Section 4: Speaking".
  const SPEAKING_TYPE_LABELS = {
    Interview: 'Interview', ImageDescription: 'Image Description',
    ExchangeOfInformation: 'Exchange of information', AskAnswerQuestions: 'Ask questions',
    Dialogue: 'Dialogue', Monologue: 'Monologue', Expose: 'Exposé'
  };
  const speakingTypeLabel = (sectionKey === 'speaking' && item?.task) ? (SPEAKING_TYPE_LABELS[item.task.task_type] || '') : '';
  const dialogueInProgress = isDialogueTask
    && dialogueState[item.task.task_id]?.started
    && !dialogueState[item.task.task_id]?.ended;

  // Has the user actually answered the current item? Save & Next is disabled
  // until this is true (Skip stays enabled). Validation happens on Save & Next.
  const isCurrentAnswered = (() => {
    if (!item) return false;
    if (sectionKey === 'reading') {
      const v = answers[item.question?.q_id];
      return !!v && v !== '__skipped__';
    }
    if (sectionKey === 'listening') {
      // Enabled as soon as AT LEAST ONE question on the current audio page is
      // answered. Unanswered ones are saved as skipped on Save & Next.
      const page = currentListeningPage;
      if (!page) return false;
      return page.questions.some(pq => answers[pq.q_id] && answers[pq.q_id] !== '__skipped__');
    }
    if (sectionKey === 'writing' && item.task) {
      const v = answers[item.task.task_id];
      if (!v || v === '__skipped__') return false;
      if (typeof v === 'object') return Object.values(v).some(x => x != null && String(x).trim() !== '');
      return String(v).trim() !== '';
    }
    if (sectionKey === 'speaking' && item.task) {
      if (isDialogueTask) {
        // Dialogue is "answered" once it has been ended (conversation completed)
        return !!dialogueState[item.task.task_id]?.ended;
      }
      // Interview / Ask-Answer: answered once a recording exists
      return !!recordings[item.task.task_id];
    }
    return false;
  })();

  // Is the current item the last item of the CURRENT section (but not the whole exam)?
  const isLastOfSection = (() => {
    if (!currentSection) return false;
    if (sectionKey === 'listening') {
      return currentListeningPageIndex === listeningPages.length - 1;
    }
    return currentItem === sectionItems.length - 1;
  })();
  const nextSectionName = (activeSection < sections.length - 1)
    ? SECTION_LABELS[sections[activeSection + 1].key]
    : null;
  // On the last item of a non-final section, the button reads "Next section - <name>".
  const showNextSectionLabel = isLastOfSection && !isLastItemOverall && nextSectionName;

  return (
    <div className="exam-prep-page quiz-active">
      <Header user={user} />
      <div className="bw-layout">
        <SideNav onNavigate={(path) => { pendingNavRef.current = path; setShowLeaveConfirm(true); }} />
        <main className="bw-main">
          {/* Top bar */}
          <div className="bw-top-bar">
            <div className="bw-title-row">
              <span className="bw-title-purple">Exam Prep</span>
              <span className="bw-title-dash">-</span>
              <span className="bw-title-lang">{langName}</span>
              <img src={`/images/flag-${langName.toLowerCase()}.svg`} alt="" width="24" height="24" style={{ marginLeft: '8px' }} />
            </div>
            <button className="bw-exit-btn" onClick={handleExit}>Exit Exam Prep</button>
          </div>

          {/* Section tabs (BrainWarmup-style icons) + Level badge */}
          <div className="ep-sections-row">
            <div className="bw-sections-nav">
              {sections.map((s, i) => {
                // Determine icon: completed sections use Done/doneicon; current uses inprogress; future uses yettostart
                const secItems = getSectionItems(sections[i]);
                // Section is SAVED when every item has been submitted (Save & Next / Skip).
                const allSaved = secItems.length > 0 && secItems.every(it => {
                  const k = it.question?.q_id || it.task?.task_id;
                  return savedItems[k];
                });
                // GREEN tick only when every item is actually ANSWERED (none skipped).
                // If any item is skipped, use the black done tick instead.
                const allAnsweredNoSkip = allSaved && secItems.every(it => {
                  const k = it.question?.q_id || it.task?.task_id;
                  if (it.task) {
                    // Speaking: answered = recorded or dialogue completed (and not marked skipped)
                    const answeredTask = recordings[it.task.task_id] || dialogueState[it.task.task_id]?.ended || (answers[k] && answers[k] !== '__skipped__');
                    return answeredTask && answers[k] !== '__skipped__';
                  }
                  return answers[k] && answers[k] !== '__skipped__';
                });
                // Section is "attempted" if at least one of its items is saved
                // (answered OR skipped — a skip still counts as attempted).
                const attempted = secItems.some(it => savedItems[it.question?.q_id || it.task?.task_id]);
                // Icon is driven by attempt state (NOT by the current position), so a
                // section keeps its done tick even after you navigate back to an earlier one.
                //   - fully answered, no skips -> green Done
                //   - attempted (some/all skipped or partially saved) -> black done
                //   - the section you're currently on and not yet attempted -> in progress
                //   - otherwise -> yet to start
                let icon;
                if (allAnsweredNoSkip) icon = '/images/Done.svg';
                else if (allSaved || attempted) icon = '/images/doneicon.svg';
                else if (i === activeSection) icon = '/images/inprogressicon.svg';
                else icon = '/images/yettostarticon.svg';
                // Navigation gating: any already-attempted section is clickable (in
                // either direction), independent of the current section's position.
                // The current section isn't a click target. Future, never-attempted
                // sections stay locked until reached via Save & Next.
                const clickable = i !== activeSection && attempted;
                const completedLook = attempted || i < activeSection;
                return (
                  <React.Fragment key={s.key}>
                    {i > 0 && <div className={'bw-section-separator' + (completedLook || i <= activeSection ? ' completed' : '')}></div>}
                    <div
                      className={'bw-section-tab ' + (i === activeSection ? 'active ' : '') + (completedLook ? 'completed ' : '') + (i !== activeSection && !clickable ? 'locked' : '')}
                      onClick={() => { if (clickable) goToSection(i); }}
                      style={{ cursor: clickable ? 'pointer' : 'default' }}>
                      <img src={icon} alt="" width="20" height="20" />
                      <span>{i + 1}. {SECTION_LABELS[s.key]}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <span className="ep-level-badge">Level {mapLevel(proficiencyLevel)}</span>
          </div>

          {/* Timer */}
          {prepActive ? (
            <div className="bw-timer-row ep-prep-timer-row">
              {/* Preparation timer (active) */}
              <span className="bw-timer-label ep-prep-label">Preparation time remaining</span>
              <span className="bw-timer-value ep-prep-value">{formatTime(prepTimeLeft)}</span>
              <div className="bw-timer-bar ep-prep-bar">
                <div className="bw-timer-fill ep-prep-fill" style={{ width: (prepTimeLeft / Math.max(1, activePrepDurationRef.current)) * 100 + '%' }}></div>
              </div>
              {/* Main exam timer — paused */}
              <span className="bw-timer-label ep-paused-label"><img src="/images/black-info.svg" alt="" width="13" height="13" style={{ verticalAlign: 'middle', marginRight: 4 }} onError={e => { e.target.style.display = 'none'; }} />Time Paused</span>
              <span className="bw-timer-value ep-paused-value">{formatTime(timeLeft)}</span>
              <div className="bw-timer-bar ep-paused-bar">
                <div className="bw-timer-fill" style={{ width: timerProgress + '%', background: '#D9B382' }}></div>
              </div>
            </div>
          ) : (
            <div className="bw-timer-row">
              <span className="bw-timer-label">Time remaining</span>
              <span className="bw-timer-value" style={{ color: timeLeft <= 600 ? '#D60D0D' : timeLeft <= 1800 ? '#C16B02' : undefined }}>
                {formatTime(timeLeft)}
              </span>
              <div className="bw-timer-bar">
                <div className="bw-timer-fill" style={{ width: timerProgress + '%', background: timeLeft <= 600 ? '#D60D0D' : timeLeft <= 1800 ? '#C16B02' : undefined }}></div>
              </div>
            </div>
          )}

          {/* Content card */}
          <div className={'bw-content-card' + (isDialogueTask ? ' ep-dialogue-card' : '')}>
            {!isDialogueTask && (
              <div className="ep-section-head">
                <h2 className="ep-section-title-row">
                  Section {activeSection + 1}: <span className="ep-section-title-purple">{SECTION_LABELS[sectionKey]}{speakingTypeLabel ? ` - ${speakingTypeLabel}` : ''}</span>
                </h2>
              </div>
            )}

            {/* Listening Instructions — shown right after the section header, before the audio task */}
            {sectionKey === 'listening' && currentListeningPage && (
              <div className="ep-speak-instruction ep-listen-instruction">
                <div className="ep-speak-instruction-title"><img src="/images/purpleInfo.svg" alt="" width="14" height="14" /> Listening Instructions</div>
                <p className="ep-listen-instruction-text">Each audio can be played a maximum of two times. We recommend reading all the questions first, since the audio cannot be paused during playback. Each audio will have a set of questions; you may answer them or skip and move to next.</p>
              </div>
            )}

            {/* Task label + instruction (from the passage/audio/task) */}
            {/* Shared task label + instruction. Hidden for dialogue and the whole
                speaking section (speaking renders its own label + instructions inside
                the ep-speaking block, right below the section header). */}
            {!isDialogueTask && sectionKey !== 'speaking' && sectionKey !== 'writing' && (
              <p className="ep-task-line">
                {(item?.passage?.task_label || item?.audio?.task_label || item?.task?.task_label) && (
                  <strong>{item?.passage?.task_label || item?.audio?.task_label || item?.task?.task_label}</strong>
                )}
                {(item?.passage?.task_instruction || item?.audio?.task_instruction || item?.task?.task_instruction || currentSection?.data?.global_instruction) && (
                  <> {(item?.passage?.task_label || item?.audio?.task_label || item?.task?.task_label) ? '- ' : ''}{item?.passage?.task_instruction || item?.audio?.task_instruction || item?.task?.task_instruction || currentSection?.data?.global_instruction}</>
                )}
              </p>
            )}

            {/* Counter + Mark badge row (hidden for dialogue and speaking — speaking
                shows its own "Fragen N:" label inside the ep-speaking block) */}
            {!isDialogueTask && sectionKey !== 'speaking' && (
            <div className="ep-counter-row">
              <span className="ep-fragen">
                {sectionKey === 'listening'
                  ? `Audio ${currentListeningPageIndex + 1} of ${listeningPages.length}`
                  : `${QUESTION_WORD[language] || 'Question'} ${currentItem + 1} of ${sectionItems.length}`}
              </span>
              {/* Reading: fixed 1 mark badge beside the counter. Listening shows it
                  per-question instead. */}
              {sectionKey === 'reading' && (
                <span className="ep-mark-badge">{marksLabel(READING_LISTENING_MARK)}</span>
              )}
              {/* Writing shows task marks here; speaking shows it next to the task
                  label below (so it's not duplicated in both places). */}
              {sectionKey === 'writing' && getMarks(item?.task) != null && (
                <span className="ep-mark-badge">{marksLabel(getMarks(item.task))}</span>
              )}
            </div>
            )}

            {/* Unavailable section fallback */}
            {currentSection?.data?.unavailable && (
              <div className="ep-unavailable">
                <img src="/images/black-info.svg" alt="" width="20" height="20" />
                <p>This section could not be generated for this exam. You can continue to the next section.</p>
              </div>
            )}

            {/* READING */}
            {sectionKey === 'reading' && item && (
              <div className="ep-reading">
                {renderPassageBox(item, { question: q, sectionKey: 'reading', id: q.q_id })}
                <div className="bw-question-area">
                  {renderNoticeCard(q)}
                  <p className="bw-question-text">{item.question.question_num}. {questionText(q)}</p>
                  <div className="bw-options-section">
                    <div className="bw-options-label"><span className="bw-required">*</span> Choose your answer</div>
                    {hasRichOptions(q) ? (
                      renderRichOptions(q)
                    ) : (
                      (q.options || []).map((opt, idx) => {
                        const letter = ['A', 'B', 'C', 'D'][idx];
                        const optLabel = (opt && typeof opt === 'object') ? (opt.text || opt.label || opt.value || '') : opt;
                        const val = /^(Richtig|Falsch)$/.test(optLabel) ? optLabel : letter;
                        return (
                          <label key={idx} className={'bw-option ' + (answers[q.q_id] === val ? 'selected' : '')}>
                            <input type="radio" name={q.q_id} checked={answers[q.q_id] === val} onChange={() => selectAnswer(q.q_id, val)} />
                            <span className="bw-radio-custom"></span>
                            <span className="bw-option-text">{optionDisplay(letter, optLabel)}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {!hasRichOptions(q) && renderOptionDetails(q)}
                  <div className="ep-read-aloud" onClick={() => handleReadAloud(q.q_id, buildQuestionReadText(q))}>
                    <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                    <span>{ttsPlayingId === q.q_id ? 'Stop' : 'Read aloud'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* LISTENING — one audio per page, all its questions in a scroll area */}
            {sectionKey === 'listening' && currentListeningPage && (
              <div className="ep-listening">
                <div className="ep-audio-block">
                  <button className="bw-play-btn" onClick={() => handlePlayAudio(currentListeningPage.audio)} disabled={(playsLeft[currentListeningPage.audio.audio_id] ?? 2) <= 0 && !isPlaying}>
                    {isPlaying ? <span className="pause-icon"></span> : <span className="play-icon"></span>}
                  </button>
                  <span className="ep-audio-time">
                    {formatTime(Math.round(audioCurrentTime))} / {formatTime(Math.round(audioDuration || Number(currentListeningPage.audio.duration || currentListeningPage.audio.duration_seconds || 0)))}
                  </span>
                  <div className="ep-audio-track">
                    <div className="bw-waveform-container">
                      <svg width="100%" height="24" viewBox="0 0 350 24" preserveAspectRatio="none">
                        {Array.from({ length: 80 }).map((_, i) => {
                          const heights = [4, 8, 12, 16, 20, 24, 20, 16, 12, 8, 4, 8, 12, 16, 20, 16, 12, 8, 4, 8];
                          const h = heights[i % heights.length];
                          const played = i < (audioProgress / 100) * 80;
                          return <rect key={i} x={i * 4.375} y={(24 - h) / 2} width="2" height={h} fill={played ? '#7500C0' : '#707070'} />;
                        })}
                      </svg>
                    </div>
                  </div>
                  <div className="bw-plays-left"><strong>{playsLeft[currentListeningPage.audio.audio_id] ?? 2}</strong> / 2 plays left</div>
                </div>
                <div className="bw-divider"></div>
                {renderPassageBox({ audio: currentListeningPage.audio })}

                {/* All questions for this audio, scrollable */}
                <div className="ep-listening-questions" ref={listeningQuestionsRef}>
                  {currentListeningPage.questions.map((lq, qi) => (
                    <div key={lq.q_id} className={'bw-question-area' + (qi > 0 ? ' ep-listening-q-sep' : '')}>
                      {renderPassageBox({ question: lq }, { question: lq, sectionKey: 'listening', id: lq.q_id })}
                      {renderNoticeCard(lq)}
                      <div className="ep-q-head">
                        <p className="bw-question-text">{lq.question_num}. {questionText(lq)}</p>
                        <span className="ep-mark-badge">{marksLabel(READING_LISTENING_MARK)}</span>
                      </div>
                      <div className="bw-options-section">
                        <div className="bw-options-label"><span className="bw-required">*</span> Choose your answer</div>
                        {hasRichOptions(lq) ? (
                          renderRichOptions(lq)
                        ) : (
                          (lq.options || []).map((opt, idx) => {
                            const letter = ['A', 'B', 'C', 'D'][idx];
                            const optLabel = (opt && typeof opt === 'object') ? (opt.text || opt.label || opt.value || '') : opt;
                            const val = /^(Richtig|Falsch)$/.test(optLabel) ? optLabel : letter;
                            return (
                              <label key={idx} className={'bw-option ' + (answers[lq.q_id] === val ? 'selected' : '')}>
                                <input type="radio" name={lq.q_id} checked={answers[lq.q_id] === val} onChange={() => selectAnswer(lq.q_id, val)} />
                                <span className="bw-radio-custom"></span>
                                <span className="bw-option-text">{optionDisplay(letter, optLabel)}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {!hasRichOptions(lq) && renderOptionDetails(lq)}
                      <div className="ep-read-aloud" onClick={() => handleReadAloud(lq.q_id, buildQuestionReadText(lq))}>
                        <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                        <span>{ttsPlayingId === lq.q_id ? 'Stop' : 'Read aloud'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WRITING */}
            {sectionKey === 'writing' && item && item.task && (
              <div className="ep-writing">
                {item.task.scenario && <div className="ep-scenario">{item.task.scenario}</div>}

                {/* FORM FILLING task */}
                {item.task.task_type === 'FormFilling' && item.task.form ? (
                  <div className="ep-form">
                    <div className="ep-form-head">
                      <img src="/images/icon-assignment.svg" alt="" width="18" height="18" />
                      <div>
                        <div className="ep-form-title">{item.task.form.title}</div>
                        {item.task.form.subtitle && <div className="ep-form-subtitle">{item.task.form.subtitle}</div>}
                      </div>
                    </div>
                    <div className="ep-form-grid">
                      {(item.task.form.fields || []).map(field => {
                        const taskAns = (answers[item.task.task_id] && typeof answers[item.task.task_id] === 'object') ? answers[item.task.task_id] : {};
                        const isTarget = field.target && field.editable;
                        return (
                          <div key={field.id} className={'ep-form-field' + (field.input_type === 'radio' || field.input_type === 'signature' ? ' full' : '')}>
                            <label className="ep-form-label">
                              {isTarget && <span className="bw-required">*</span>}{field.label}
                            </label>

                            {/* Non-editable pre-filled field */}
                            {!isTarget && field.input_type === 'signature' && (
                              <div className="ep-form-signature">{field.value}</div>
                            )}
                            {!isTarget && field.input_type !== 'signature' && field.input_type !== 'radio' && (
                              <div className="ep-form-readonly">{field.value}</div>
                            )}

                            {/* Editable target fields */}
                            {isTarget && field.input_type === 'radio' && (
                              <div className="ep-form-radios">
                                {(field.options || []).map(opt => (
                                  <label key={opt} className="ep-form-radio">
                                    <input type="radio" name={item.task.task_id + '_' + field.id}
                                      checked={taskAns[field.id] === opt}
                                      onChange={() => setFormField(item.task.task_id, field.id, opt)} />
                                    <span>{opt}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {isTarget && field.input_type !== 'radio' && (
                              <div className="ep-form-input-wrap">
                                <input
                                  className="ep-form-input"
                                  type="text"
                                  inputMode={field.input_type === 'number' ? 'numeric' : 'text'}
                                  value={taskAns[field.id] || ''}
                                  onChange={e => {
                                    let v = e.target.value;
                                    if (field.input_type === 'number') v = v.replace(/[^0-9]/g, '');
                                    setFormField(item.task.task_id, field.id, v);
                                  }}
                                  placeholder={field.input_type === 'date' ? 'DD.MM.YYYY' : ''}
                                />
                                {/* pencil shows only after the field has a value */}
                                {taskAns[field.id] && String(taskAns[field.id]).trim() !== '' && (
                                  <svg className="ep-form-edit-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 20h4l10-10-4-4L4 16v4Z" stroke="#7500C0" strokeWidth="1.6" strokeLinejoin="round" />
                                    <path d="M14 6l4 4" stroke="#7500C0" strokeWidth="1.6" strokeLinecap="round" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  /* Free-text writing task (ShortMessage etc.) */
                  <>
                    <p className="bw-question-text"><strong>{item.task.task_label}</strong></p>
                    <p className="bw-question-text">{item.task.task_instruction}</p>
                    {item.task.content_points && (
                      <ul className="ep-writing-points">
                        {item.task.content_points.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    )}
                    <div className="bw-writing-label">
                      <span className="bw-required">*</span> Write your response below
                    </div>
                    {(() => {
                      // Character limit by proficiency level: A1/A2 -> 500, B1/B2 (and higher) -> 1500.
                      const lvl = String(mapLevel(proficiencyLevel) || '').toUpperCase();
                      const maxChars = (lvl === 'A1' || lvl === 'A2') ? 500 : 1500;
                      const val = typeof answers[item.task.task_id] === 'string' ? answers[item.task.task_id] : '';
                      const left = Math.max(0, maxChars - val.length);
                      const atLimit = val.length >= maxChars;
                      return (
                        <>
                          <textarea
                            className="bw-writing-area"
                            placeholder="Write your response here..."
                            value={val}
                            maxLength={maxChars}
                            onChange={(e) => selectAnswer(item.task.task_id, e.target.value.slice(0, maxChars))}
                            onPaste={(e) => {
                              // Cap pasted content so it never exceeds the limit
                              const paste = (e.clipboardData || window.clipboardData).getData('text');
                              const room = maxChars - val.length;
                              if (paste.length > room) {
                                e.preventDefault();
                                const next = (val + paste).slice(0, maxChars);
                                selectAnswer(item.task.task_id, next);
                              }
                            }}
                            rows={7}
                          />
                          <div className="bw-writing-footer">
                            <span className="bw-clear-response" onClick={() => selectAnswer(item.task.task_id, '')}>Clear Response</span>
                            <span className={'bw-char-count' + (atLimit ? ' at-limit' : '')}>
                              <img src="/images/yellowwarning.svg" alt="" width="14" height="14" style={{ marginRight: '4px', verticalAlign: 'middle' }} onError={e => { e.target.style.display = 'none'; }} />
                              {atLimit ? 'Character limit reached' : `${left} Character left`}
                              &nbsp;&nbsp;<strong>{val.length}</strong> / {maxChars} characters
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}

                {/* Read Aloud — reads the writing task label + instruction + points */}
                <div className="ep-read-aloud" onClick={() => handleReadAloud(item.task.task_id, buildTaskReadText(item.task))}>
                  <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                  <span>{ttsPlayingId === item.task.task_id ? 'Stop' : 'Read aloud'}</span>
                </div>
              </div>
            )}

            {/* SPEAKING */}
            {sectionKey === 'speaking' && item && item.task && (() => {
              const task = item.task;
              const recDur = task.recording_duration_sec || 120;
              const recMax = `${Math.floor(recDur / 60)}:${String(recDur % 60).padStart(2, '0')}`;
              const recNow = `${Math.floor(recordingTime / 60)}:${String(recordingTime % 60).padStart(2, '0')}`;
              // Localized speaking-control strings for the exam language (English fallback).
              const spk = SPEAK_STRINGS[language] || SPEAK_STRINGS.en;
              const isDialogue = task.task_type === 'Dialogue' || task.mode === 'avatar_dialogue';
              // Exchange-of-information / Ask-Answer = selectable topic chips
              // "Select N chips" tasks — only these require the user to pick chips
              // before recording (driven by prompt_style / chips_to_select, e.g.
              // AskAnswerQuestions with "select_2_from_4_chips"). Exchange of
              // Information just displays its chips (no selection required).
              const chipsToSelect = Number(task.chips_to_select || 0);
              const isAsk = task.prompt_style === 'select_2_from_4_chips'
                || chipsToSelect > 0
                || (task.task_type === 'AskAnswerQuestions' && !task.prompt_style);
              const isImage = task.task_type === 'ImageDescription' || task.mode === 'single_recording_with_image' || task.image_required;
              // Monologue / Expose = pick a scenario/topic then record a monologue
              const isMonologue = task.task_type === 'Monologue' || task.task_type === 'Expose';
              // Human-friendly section sub-title per task type
              const TASK_TYPE_LABELS = {
                Interview: 'Interview',
                ImageDescription: 'Image Description',
                ExchangeOfInformation: 'Exchange of information',
                AskAnswerQuestions: 'Ask questions',
                Dialogue: 'Dialogue',
                Monologue: 'Monologue',
                Expose: 'Exposé'
              };
              const taskTypeLabel = TASK_TYPE_LABELS[task.task_type] || 'Speaking';

              // ---- Speaking Instruction (shared) ----
              const instructions = (
                <div className="ep-speak-instruction">
                  <div className="ep-speak-instruction-title"><img src="/images/purpleInfo.svg" alt="" width="14" height="14" /> Speaking Instructions</div>
                  <ol className="ep-speak-instruction-list">
                    <li>Read the prompt carefully and take a moment to organize your thoughts before you begin.</li>
                    {isDialogue
                      ? <li>Choose a role-play scenario and engage in a simulated dialogue with the avatar.</li>
                      : <li>Once you start recording, it cannot be paused or restarted. Speak clearly and naturally.</li>}
                    {isDialogue ? (() => {
                      // Both prep time and test duration come from the agent response
                      // (prep_time_sec and dialogue_duration_sec). No hardcoded values.
                      const prepMin = Math.round(getPrepSeconds(task) / 60);
                      const dd = task.dialogue_duration_sec;
                      let durLabel = null;
                      if (Array.isArray(dd) && dd.length >= 2) {
                        const lo = Math.round(dd[0] / 60);
                        const hi = Math.round(dd[1] / 60);
                        durLabel = lo === hi ? `${hi}` : `${lo}-${hi}`;
                      } else if (Number.isFinite(Number(dd))) {
                        durLabel = `${Math.round(Number(dd) / 60)}`;
                      }
                      return (
                        <li><strong>
                          {prepMin > 0 ? `Preparation Time: ${prepMin} minute${prepMin > 1 ? 's' : ''}` : ''}
                          {prepMin > 0 && durLabel ? ' | ' : ''}
                          {durLabel ? `Test Duration: ${durLabel} minute${durLabel === '1' ? '' : 's'}` : ''}
                        </strong></li>
                      );
                    })() : getPrepSeconds(task) > 0 ? (
                      <li><strong>Preparation Time: {Math.floor(getPrepSeconds(task) / 60)} minutes | Test Duration: {Math.round((task.recording_duration_sec || 120) / 60)} minutes</strong></li>
                    ) : null}
                    <li>Your response will be submitted automatically when the recording time ends.</li>
                  </ol>
                </div>
              );

              // =========== DIALOGUE (split layout) ===========
              if (isDialogue) {
                const chosen = selectedScenario[task.task_id];
                const chosenScn = (task.scenarios || []).find((s, idx) => (s.id || `scn_${idx}`) === chosen);
                const started = dialogueState[task.task_id]?.started;
                const ended = dialogueState[task.task_id]?.ended;
                // Conversation max always comes from the agent's dialogue_duration_sec[1].
                const dlgMaxSec = getDialogueMaxSec(task);
                const dlgMaxLabel = `${Math.floor(dlgMaxSec / 60)}:${String(dlgMaxSec % 60).padStart(2, '0')}`;
                return (
                  <div className="ep-speaking ep-speaking-dialogue">
                    <div className="ep-dlg-left">
                      <div className="ep-section-head">
                        <h2 className="ep-section-title-row">
                          Section {activeSection + 1}: <span className="ep-section-title-purple">{SECTION_LABELS[sectionKey]}{taskTypeLabel ? ` - ${taskTypeLabel}` : ''}</span>
                        </h2>
                      </div>
                      {instructions}
                      <div className="ep-counter-inline ep-speak-qrow">
                        <span className="ep-speak-qlabel"><strong>{QUESTION_WORD[language] || 'Question'} {currentItem + 1}:</strong> {task.task_label}</span>
                        <div className="ep-speak-qright">
                          {getMarks(task) != null && <span className="ep-mark-badge">{marksLabel(getMarks(task))}</span>}
                          <div className="ep-read-aloud ep-speak-read" onClick={() => handleReadAloud(task.task_id, buildTaskReadText(task))}>
                            <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                            <span>{ttsPlayingId === task.task_id ? 'Stop' : 'Read aloud'}</span>
                          </div>
                        </div>
                      </div>
                      <p className="ep-speak-prompt">{task.task_instruction}</p>

                      {/* Scenario selection */}
                      <div className="ep-dlg-scenarios">
                        {(task.scenarios || []).map((scn, idx) => {
                          const sid = scn.id || `scn_${idx}`;
                          return (
                          <div key={sid}
                            className={'ep-dlg-scenario' + (chosen === sid ? ' selected' : '') + (started ? ' locked' : '')}
                            onClick={() => { if (!started) setSelectedScenario(prev => ({ ...prev, [task.task_id]: sid })); }}>
                            <span className={'ep-dlg-radio' + (chosen === sid ? ' checked' : '')}></span>
                            <div className="ep-dlg-scenario-body">
                              <span className="ep-dlg-scenario-label">SUJET {idx + 1}</span>
                              <div className="ep-dlg-scenario-title">{scn.title}</div>
                              {/* Show only the situation text (situation or situation_description) —
                                  no avatar_role / preparation_notes / other keys. */}
                              {(scn.situation || scn.situation_description) && (
                                <p className="ep-dlg-scenario-text">{scn.situation || scn.situation_description}</p>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Right avatar panel */}
                    <div className="ep-dlg-right">
                      {/* BS-style avatar cards */}
                      <div className="bsc-avatars ep-dlg-bsc-avatars">
                        {/* Partner (agent) avatar */}
                        <div className={`bsc-avatar-card${dialogueBusy || (!dlgMicActive && started) ? ' highlighted' : ''}`}>
                          <div className="bsc-avatar-circle soren">
                            <img src="/images/MaleAvatar.svg" alt={chosenScn?.avatar_role || 'Avatar'} className="bsc-avatar-inner" />
                          </div>
                          <div className="bsc-avatar-bottom-icon">
                            <img src={dlgAvatarMuted ? '/images/speaker-mute.svg' : '/images/speakerblack.svg'} alt="" width="40" height="40"
                              onClick={() => started && !ended && toggleDlgSpeaker(task)}
                              style={{ cursor: started && !ended && dlgLastReplyRef.current ? 'pointer' : 'default', opacity: started && !ended && dlgLastReplyRef.current ? 1 : 0.4 }} />
                          </div>
                          <p className="bsc-avatar-name">{chosenScn?.avatar_role || 'Avatar'}</p>
                        </div>

                        {/* User avatar */}
                        <div className={`bsc-avatar-card${dlgMicActive ? ' highlighted' : ''}`}>
                          <div className="bsc-avatar-circle victor">
                            {dlgCameraOn ? (
                              <video ref={dlgVideoRef} autoPlay muted playsInline className="bsc-camera-feed" />
                            ) : (
                              <span className="bsc-avatar-vs">{getUserInitials(user)}</span>
                            )}
                          </div>
                          <div className="bsc-avatar-bottom-icons">
                            <button className="bsc-mic-btn" disabled={!started || ended || dialogueBusy}
                              onClick={(e) => { e.stopPropagation(); if (started && !ended && !dialogueBusy) toggleDlgMic(task); }}
                              style={{ opacity: started && !ended ? 1 : 0.4, cursor: started && !ended ? 'pointer' : 'default' }}>
                              <span className="bsc-icon-circle"><img src="/images/Ellipseblack.svg" alt="" className="bsc-icon-circle-bg" /><img src={dlgMicActive ? '/images/unmuteMic-voice.svg' : '/images/Mic-voice.svg'} alt="Mic" width="16" height="16" className="bsc-icon-center" /></span>
                            </button>
                            <button className="bsc-mic-btn" disabled={!started || ended}
                              onClick={(e) => { e.stopPropagation(); if (started && !ended) toggleDlgCamera(); }}
                              style={{ opacity: started && !ended ? 1 : 0.4, cursor: started && !ended ? 'pointer' : 'default' }}>
                              <span className="bsc-icon-circle"><img src="/images/Ellipseblack.svg" alt="" className="bsc-icon-circle-bg" /><img src={dlgCameraOn ? '/images/camaraicon.svg' : '/images/stopcamaraicon.svg'} alt="Camera" width="16" height="16" className="bsc-icon-center" /></span>
                            </button>
                          </div>
                          <p className="bsc-avatar-name">{getUserFirstName(user)}</p>
                        </div>
                      </div>

                      {/* Live caption of latest exchange */}
                      {started && (
                        <div className="ep-dlg-caption">
                          {dlgMicActive && dialogueInput ? (
                            /* Live user speech while the mic is on */
                            <p className="bsc-agent-response user"><strong>{getUserFirstName(user)}:</strong> {dialogueInput}{dialogueListening && <em> (speaking...)</em>}</p>
                          ) : dialogueBusy ? (
                            /* Waiting for agent — 3 dots */
                            <div className="bsc-agent-response loading"><span className="bsc-loading-dots"><span></span><span></span><span></span></span></div>
                          ) : (() => {
                            /* Show the latest message (agent or user) */
                            const turns = dialogueState[task.task_id]?.turns || [];
                            const last = turns[turns.length - 1];
                            if (!last) return null;
                            return (
                              <p className={'bsc-agent-response ' + (last.role === 'avatar' ? 'assistant' : 'user')}>
                                <strong>{last.role === 'avatar' ? (chosenScn?.avatar_role || 'Partner') : getUserFirstName(user)}:</strong> {last.text}
                              </p>
                            );
                          })()}
                        </div>
                      )}

                      {!started ? (
                        <>
                          <p className="ep-dlg-hint">Engage in a conversation of {(Array.isArray(task.dialogue_duration_sec) && task.dialogue_duration_sec.length >= 2) ? `${Math.round(task.dialogue_duration_sec[0] / 60)} to ${Math.round(task.dialogue_duration_sec[1] / 60)} minutes` : `${Math.round(dlgMaxSec / 60)} minutes`} with the avatar on the topic you have chosen.</p>
                          <div className="ep-dlg-start-row">
                            {/* Red "Start Conversation" */}
                            <button className="ep-dlg-conv-btn start" disabled={!chosen || dialogueBusy} onClick={() => startDialogue(task, chosen)}>
                              {dialogueBusy ? 'Starting...' : 'Start Conversation'}
                            </button>
                            <span className="ep-record-timer">0:00 / {dlgMaxLabel}</span>
                          </div>
                        </>
                      ) : !ended ? (
                        <div className="ep-dlg-start-row">
                          {/* Black "Stop Conversation" while the conversation is running */}
                          <button className="ep-dlg-conv-btn recording" disabled={dialogueBusy || dlgMicActive} onClick={() => endDialogue(task)}>Stop Conversation</button>
                          <span className="ep-record-timer">{Math.floor(dialogueElapsed / 60)}:{String(dialogueElapsed % 60).padStart(2, '0')} / {dlgMaxLabel}</span>
                        </div>
                      ) : (
                        <>
                          {/* Ended: green "Recording completed" + playback of the full conversation */}
                          {recordings[task.task_id] && (
                            <div className="ep-playback-bar ep-dlg-playback">
                              <button className="ep-playback-btn" onClick={() => togglePlayback(recordings[task.task_id].url)}>
                                {isPlayingBack ? <span className="ep-pb-pause"></span> : <span className="ep-pb-play"></span>}
                              </button>
                              <div className="ep-playback-wave">
                                <svg width="100%" height="28" viewBox="0 0 600 28" preserveAspectRatio="none">
                                  {Array.from({ length: 120 }).map((_, i) => {
                                    const heights = [6, 12, 18, 24, 20, 14, 8, 16, 22, 26, 18, 10, 6, 14, 20, 24, 16, 10, 8, 18];
                                    const h = heights[i % heights.length];
                                    const played = i < (playbackProgress / 100) * 120;
                                    return <rect key={i} x={i * 5} y={(28 - h) / 2} width="2.5" height={h} fill={played ? '#7500C0' : '#B8B8B8'} />;
                                  })}
                                </svg>
                              </div>
                              <span className="ep-playback-time">{fmtRecorded(recordings[task.task_id].time)}</span>
                            </div>
                          )}
                          <div className="ep-dlg-completed">
                            <img src="/images/doneicon.svg" alt="" width="16" height="16" />
                            <span>Recording completed</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              }

              // =========== INTERVIEW & ASK/ANSWER (centered mic) ===========
              const rec = recordings[task.task_id];
              const recSeconds = rec?.time != null ? rec.time : recordingTime;
              // Monologue/Expose: a scenario/topic must be picked before recording.
              // Topics can arrive as `topics` (list), `scenarios` (older list), or a
              // single `topic` object (newer Monologue shape). Normalize to a list so
              // the same card rendering (which prints question_detail) applies to all.
              const monoTopics = (Array.isArray(task.topics) && task.topics.length ? task.topics
                : Array.isArray(task.scenarios) && task.scenarios.length ? task.scenarios
                : (task.topic && typeof task.topic === 'object') ? [task.topic]
                : (typeof task.topic === 'string' && task.topic.trim()) ? [{ id: 'topic_1', title: task.topic, question: task.topic }]
                : []);
              const hasTopics = isMonologue && monoTopics.length > 0;
              // With a single topic there's nothing to choose, so auto-select it.
              const singleTopicId = (hasTopics && monoTopics.length === 1)
                ? (monoTopics[0].id || 'topic_0') : null;
              const scnChosen = selectedScenario[task.task_id] || singleTopicId;
              const needsScenario = hasTopics;
              const chipsNeeded = chipsToSelect || 2;
              const recordBlocked =
                (isAsk && (selectedTopics[task.task_id] || []).length < chipsNeeded) ||
                (needsScenario && !scnChosen);
              // Preparation time per task type:
              //   Interview / ImageDescription / AskAnswerQuestions -> none
              //   ExchangeOfInformation / Dialogue / Monologue       -> 10 min
              //   Exposé                                             -> 30 min
              const prepRunningHere = prepActive && prepTaskId === task.task_id;

              // =========== MONOLOGUE / EXPOSÉ (topic cards left, recording right) ===========
              if (needsScenario) {
                const isExpose = task.task_type === 'Expose';
                const locked = !!rec || isRecording;
                // Once locked (recording/recorded), only show the chosen topic card.
                const visibleTopics = locked ? monoTopics.filter((tp, idx) => (tp.id || `topic_${idx}`) === scnChosen) : monoTopics;
                return (
                  <div className="ep-speaking">
                    {instructions}

                    <div className="ep-counter-inline ep-speak-qrow">
                      <span className="ep-speak-qlabel"><strong>{QUESTION_WORD[language] || 'Question'} {currentItem + 1}:</strong> {task.task_label}</span>
                      <div className="ep-speak-qright">
                        {getMarks(task) != null && <span className="ep-mark-badge">{marksLabel(getMarks(task))}</span>}
                        <div className="ep-read-aloud ep-speak-read" onClick={() => handleReadAloud(task.task_id, buildTaskReadText(task))}>
                          <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                          <span>{ttsPlayingId === task.task_id ? 'Stop' : 'Read aloud'}</span>
                        </div>
                      </div>
                    </div>
                    {task.task_instruction && <p className="ep-speak-prompt">{task.task_instruction}</p>}

                    <div className="ep-divider-line"></div>

                    {/* Split: topic cards on the left, recording controls on the right */}
                    <div className="ep-mono-layout">
                      <div className="ep-mono-left">
                        {visibleTopics.map((tp) => {
                          const realIdx = monoTopics.indexOf(tp);
                          const sid = tp.id || `topic_${realIdx}`;
                          const selected = scnChosen === sid;
                          const expandKey = `${task.task_id}:${sid}`;
                          const expanded = !!expandedScenario[expandKey];
                          // German Monologue delivers the topic description under
                          // `structured_prompts` (string, array of strings, or array of
                          // objects with a description/text/prompt field). Flatten it to text.
                          const structuredText = (() => {
                            const sp = tp.structured_prompts || tp.structuredPrompts;
                            if (!sp) return '';
                            if (typeof sp === 'string') return sp;
                            if (Array.isArray(sp)) {
                              return sp.map(x => (typeof x === 'string')
                                ? x
                                : (x?.description || x?.text || x?.prompt || x?.value || ''))
                                .filter(Boolean).join('\n');
                            }
                            if (typeof sp === 'object') return sp.description || sp.text || sp.prompt || '';
                            return '';
                          })();
                          // Both Monologue and Exposé use `question_detail` as the card body,
                          // falling back to the German structured_prompts description.
                          const detailText = tp.question_detail || structuredText || tp.document_declencheur || tp.document_text || tp.situation_description || tp.situation || tp.details || '';
                          return (
                            <div key={sid}
                              className={'ep-mono-scenario' + (selected ? ' selected' : '') + (locked ? ' locked' : '')}
                              onClick={() => { if (!locked) setSelectedScenario(prev => ({ ...prev, [task.task_id]: sid })); }}>
                              {!locked && <span className={'ep-dlg-radio' + (selected ? ' checked' : '')}></span>}
                              <div className="ep-mono-scenario-body">
                                <span className="ep-mono-scenario-label">{tp.topic_label || `SUJET ${realIdx + 1}`}</span>
                                <div className="ep-mono-scenario-title">{tp.title}</div>
                                {detailText && (
                                  isExpose ? (
                                    <>
                                      <p className={'ep-mono-scenario-text' + (!expanded ? ' clamped' : '')}>{detailText}</p>
                                      {detailText.length > 140 && (
                                        <span className="ep-mono-viewmore"
                                          onClick={(e) => { e.stopPropagation(); setExpandedScenario(prev => ({ ...prev, [expandKey]: !expanded })); }}>
                                          {expanded ? 'View less' : 'View more'}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <p className="ep-mono-scenario-text">{detailText}</p>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Right: recording controls */}
                      <div className="ep-mono-right">
                        {rec ? (
                          <>
                            <div className="ep-playback-bar">
                              <button className="ep-playback-btn" onClick={() => togglePlayback(rec.url)}>
                                {isPlayingBack ? <span className="ep-pb-pause"></span> : <span className="ep-pb-play"></span>}
                              </button>
                              <div className="ep-playback-wave">
                                <svg width="100%" height="28" viewBox="0 0 600 28" preserveAspectRatio="none">
                                  {Array.from({ length: 120 }).map((_, i) => {
                                    const heights = [6, 12, 18, 24, 20, 14, 8, 16, 22, 26, 18, 10, 6, 14, 20, 24, 16, 10, 8, 18];
                                    const h = heights[i % heights.length];
                                    const played = i < (playbackProgress / 100) * 120;
                                    return <rect key={i} x={i * 5} y={(28 - h) / 2} width="2.5" height={h} fill={played ? '#7500C0' : '#B8B8B8'} />;
                                  })}
                                </svg>
                              </div>
                              <span className="ep-playback-time">{fmtRecorded(recSeconds)}</span>
                            </div>
                            <div className="ep-speak-record-row">
                              <button className="ep-btn-record-done" disabled>Recording completed</button>
                              <span className="ep-record-timer">{Math.floor(recSeconds / 60)}:{String(recSeconds % 60).padStart(2, '0')} / {recMax}</span>
                            </div>
                            <p className="ep-speak-record-hint">Votre enregistrement est terminé. Vous pouvez l'écouter avant de continuer.</p>
                          </>
                        ) : (
                          <>
                            <p className="ep-mono-record-hint">Appuyez pour enregistrer. Vous avez <strong>{Math.round(recDur / 60)} minute{recDur >= 120 ? 's' : ''}</strong> pour répondre.</p>
                            <div className="ep-speak-record-row">
                              {!isRecording ? (
                                <button className="ep-btn-record-plain" disabled={recordBlocked}
                                  onClick={() => startRecording(task.task_id, recDur)}>Start Recording</button>
                              ) : (
                                <button className="ep-btn-record stop" onClick={stopRecording}>Stop Recording</button>
                              )}
                              <span className="ep-record-timer">{isRecording ? recNow : '0:00'} / {recMax}</span>
                            </div>
                            {recordBlocked && !isRecording && (
                              <p className="ep-mono-record-note">Choisissez un sujet pour commencer. (Select a topic to start recording.)</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="ep-speaking">
                  {instructions}

                  {/* Playback bar (after recording) OR centered mic */}
                  {rec ? (
                    <div className="ep-playback-bar">
                      <button className="ep-playback-btn" onClick={() => togglePlayback(rec.url)}>
                        {isPlayingBack ? <span className="ep-pb-pause"></span> : <span className="ep-pb-play"></span>}
                      </button>
                      <div className="ep-playback-wave">
                        <svg width="100%" height="28" viewBox="0 0 600 28" preserveAspectRatio="none">
                          {Array.from({ length: 120 }).map((_, i) => {
                            const heights = [6, 12, 18, 24, 20, 14, 8, 16, 22, 26, 18, 10, 6, 14, 20, 24, 16, 10, 8, 18];
                            const h = heights[i % heights.length];
                            const played = i < (playbackProgress / 100) * 120;
                            return <rect key={i} x={i * 5} y={(28 - h) / 2} width="2.5" height={h} fill={played ? '#7500C0' : '#B8B8B8'} />;
                          })}
                        </svg>
                      </div>
                      <span className="ep-playback-time">{fmtRecorded(recSeconds)}</span>
                    </div>
                  ) : (
                    <div className="ep-speak-mic-wrap">
                      <div className={'ep-speak-mic' + (isRecording ? ' recording' : '')}>
                        <img src="/images/mic-icon.svg" alt="" onError={e => { e.target.style.display = 'none'; }} />
                      </div>
                    </div>
                  )}

                  {/* ImageDescription: image on the left, task details on the right */}
                  {isImage && (
                    <div className="ep-image-task">
                      <div className="ep-image-task-media">
                        {task.image_url
                          ? <img className="ep-image-task-img" src={task.image_url} alt={task.task_label || 'Describe this image'} />
                          : <div className="ep-image-task-img ep-image-task-placeholder">Image</div>}
                      </div>
                      <div className="ep-image-task-details">
                        <div className="ep-image-task-head ep-speak-qrow">
                          <span className="ep-speak-qlabel"><strong>{QUESTION_WORD[language] || 'Question'} {currentItem + 1}:</strong> {task.task_label}</span>
                          <div className="ep-speak-qright">
                            {getMarks(task) != null && <span className="ep-mark-badge">{marksLabel(getMarks(task))}</span>}
                            <div className="ep-read-aloud ep-speak-read" onClick={() => handleReadAloud(task.task_id, buildTaskReadText(task))}>
                              <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                              <span>{ttsPlayingId === task.task_id ? 'Stop' : 'Read aloud'}</span>
                            </div>
                          </div>
                        </div>
                        {task.task_instruction && <p className="ep-image-task-prompt">{task.task_instruction}</p>}
                        {/* image_prompt / image_prompt_hint are internal generation fields — NOT shown to learners */}
                        {task.image_instruction && <p className="ep-image-task-note">{task.image_instruction}</p>}
                      </div>
                    </div>
                  )}

                  {/* Question label + mark on the left, Read Aloud on the right */}
                  {!isImage && !needsScenario && (
                    <div className="ep-counter-inline ep-speak-qrow">
                      <span className="ep-speak-qlabel"><strong>{QUESTION_WORD[language] || 'Question'} {currentItem + 1}:</strong> {task.task_label}</span>
                      <div className="ep-speak-qright">
                        {getMarks(task) != null && <span className="ep-mark-badge">{marksLabel(getMarks(task))}</span>}
                        <div className="ep-read-aloud ep-speak-read" onClick={() => handleReadAloud(task.task_id, buildTaskReadText(task))}>
                          <img src="/images/readwithoutcolor.svg" alt="" width="16" height="16" />
                          <span>{ttsPlayingId === task.task_id ? 'Stop' : 'Read aloud'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Display-only chips (Interview, Exchange of Information, etc.) — no selection required */}
                  {!isAsk && !needsScenario && Array.isArray(task.chips) && task.chips.length > 0 && (
                    <div className="ep-chips">{task.chips.map((c, i) => <span key={i} className="ep-chip">{typeof c === 'string' ? c : (c.title || c.label || '')}</span>)}</div>
                  )}

                  {/* Interview examiner questions — only when the task has NO chips
                      (if chips exist we show those instead, to avoid duplication). */}
                  {!isAsk && !needsScenario && !(Array.isArray(task.chips) && task.chips.length > 0)
                    && Array.isArray(task.examiner_questions) && task.examiner_questions.length > 0 && (
                    <ul className="ep-examiner-questions">
                      {task.examiner_questions.map((qn, i) => <li key={i}>{qn}</li>)}
                    </ul>
                  )}

                  {/* Selectable chips (e.g. AskAnswerQuestions "select 2 of 4") — topics may come as task.topics or task.chips */}
                  {isAsk && (() => {
                    const rawTopics = (task.topics && task.topics.length ? task.topics : (task.chips || []));
                    const chosen = selectedTopics[task.task_id] || [];
                    const need = chipsToSelect || 2;
                    return (
                      <div className="ep-ask-topics-row">
                        <div className="ep-chips">
                          {rawTopics.map((t, i) => {
                            const label = (typeof t === 'string') ? t : (t.title || t.label || String(t.id) || String(i));
                            const tid = (typeof t === 'string') ? t : (t.id || t.title || String(i));
                            const sel = chosen.includes(tid);
                            return (
                              <span key={tid} className={'ep-chip selectable' + (sel ? ' selected' : '')}
                                onClick={() => toggleTopic(task.task_id, tid, need)}>
                                {label}
                              </span>
                            );
                          })}
                        </div>
                        <span className="ep-ask-count">{spk.selectedCount(chosen.length, need)}</span>
                      </div>
                    );
                  })()}

                  <div className="ep-divider-line"></div>

                  {/* Recording controls / status */}
                  <div className="ep-speak-record">
                    {rec ? (
                      <>
                        <p className="ep-speak-record-hint">{spk.recordingComplete}</p>
                        <div className="ep-speak-record-row">
                          <button className="ep-btn-record-done" disabled>Recording completed</button>
                          <span className="ep-record-timer">{Math.floor(recSeconds / 60)}:{String(recSeconds % 60).padStart(2, '0')} / {recMax}</span>
                        </div>
                      </>
                    ) : !isRecording ? (
                      <>
                        {isAsk && (selectedTopics[task.task_id] || []).length < chipsNeeded ? (
                          <p className="ep-speak-record-hint">{spk.selectTopics(chipsNeeded)}</p>
                        ) : (needsScenario && !scnChosen) ? (
                          <p className="ep-speak-record-hint">{spk.selectOneTopic}</p>
                        ) : prepRunningHere ? (
                          <p className="ep-speak-record-hint">{spk.prepRunning}</p>
                        ) : (
                          <p className="ep-speak-record-hint">{task.task_instruction || spk.tapToRecord(Math.round(recDur / 60))}</p>
                        )}

                        <div className="ep-speak-record-row">
                          <button className="ep-btn-record-plain"
                            disabled={recordBlocked}
                            onClick={() => startRecording(task.task_id, recDur)}>Start Recording</button>
                          <span className="ep-record-timer">0:00 / {recMax}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="ep-speak-record-hint">{spk.recordingInProgress}</p>
                        <div className="ep-speak-record-row">
                          <button className="ep-btn-record stop" onClick={stopRecording}>Stop Recording</button>
                          <span className="ep-record-timer">{recNow} / {recMax}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Bottom nav */}
          <div className="ep-bottom-nav">
            <div className="ep-qnav">
              <span className="ep-qnav-label">
                {sectionKey === 'listening'
                  ? `Audio ${currentListeningPageIndex + 1}/${listeningPages.length}`
                  : `Question ${currentItem + 1}/${sectionItems.length}`}
              </span>
              <div className="bw-qnav-boxes ep-qnav-grid">
                {sectionKey === 'listening'
                  ? listeningPages.map((page, i) => {
                      const allAnswered = page.questions.every(pq => answers[pq.q_id] && answers[pq.q_id] !== '__skipped__');
                      // Visited = the user has moved past/through this audio (any question
                      // has a recorded answer OR skip). If visited but not all answered, the
                      // audio counts as skipped in the bottom box.
                      const anyVisited = page.questions.some(pq => answers[pq.q_id]);
                      const partiallyDone = anyVisited && !allAnswered;
                      const isCurrent = i === currentListeningPageIndex && !(isLastItemOverall && lastItemDone);
                      const clickable = isCurrent || anyVisited;
                      return (
                        <div key={i}
                          className={'bw-qnav-box ' + (isCurrent ? 'current' : allAnswered ? 'done' : partiallyDone ? 'skipped' : '')}
                          onClick={() => { if (clickable && !isCurrent) { stopAudio(); setCurrentItem(page.startIndex); } }}
                          style={{ cursor: clickable && !isCurrent ? 'pointer' : 'default' }}>
                          {i + 1}
                        </div>
                      );
                    })
                  : sectionItems.map((it, i) => {
                      const key = it.question?.q_id || it.task?.task_id;
                      // The user's explicit Skip is the latest choice and wins the
                      // visual state — even if a recording still exists (recorded but
                      // NOT yet submitted/evaluated). So mark 'skipped' whenever the
                      // answer is '__skipped__', regardless of a lingering recording.
                      const isSkippedMark = answers[key] === '__skipped__';
                      const recorded = it.task && recordings[it.task.task_id] && !isSkippedMark;
                      // Speaking dialogue is "done" once the conversation has ended.
                      const dialogueDone = it.task && dialogueState[it.task.task_id]?.ended && !isSkippedMark;
                      const answered = answers[key] && !isSkippedMark;
                      const done = answered || recorded || dialogueDone;
                      const skipped = !done && isSkippedMark;
                      // Once the last item is saved (lastItemDone), the current item is no longer "current"
                      // so it shows green (done) — same behavior as BrainWarmup/PostAssessment
                      const isCurrent = i === currentItem && !(isLastItemOverall && lastItemDone);
                      const clickable = isCurrent || done || skipped;
                      return (
                        <div key={i}
                          className={'bw-qnav-box ' + (isCurrent ? 'current' : done ? 'done' : skipped ? 'skipped' : '')}
                          onClick={() => { if (clickable && !isCurrent) { stopAudio(); setCurrentItem(i); } }}
                          style={{ cursor: clickable && !isCurrent ? 'pointer' : 'default' }}>
                          {i + 1}
                        </div>
                      );
                    })}
              </div>
            </div>
            <div className="ep-bottom-actions">
              {/* Skip stays enabled except on the final "done" state, and is DISABLED
                  only for a speaking task that has already been SUBMITTED via Save
                  and Next (cached in speakingResults). A recorded-but-not-submitted
                  answer keeps Skip active — the user can submit it or skip it. */}
              {!(isLastItemOverall && lastItemDone) && (() => {
                // A speaking answer is "locked" (Skip disabled) only once it has been
                // submitted via Save and Next — i.e. evaluated and cached in
                // speakingResults, and NOT currently in the '__skipped__' state.
                //  - Recorded but not yet submitted -> Skip active (skipping keeps
                //    the audio so the user can return and submit it, or skip again).
                //  - Submitted via Save and Next, then returned via Previous -> Skip
                //    disabled (the evaluated answer is final; re-recording is never
                //    allowed once a recording exists).
                //  - Skipped, then returned via Previous -> Skip active.
                const spKey = item?.task ? item.task.task_id : null;
                const spSkipped = spKey && answers[spKey] === '__skipped__';
                const speakingLocked = sectionKey === 'speaking' && item?.task
                  && !!speakingResults[item.task.task_id] && !spSkipped;
                // While an audio recording or an avatar dialogue is actively in
                // progress, Skip/Previous are disabled — the user must first stop
                // recording / stop the conversation (or let it auto-stop at max
                // duration) before navigating away.
                return (
                  <button className="ep-btn-skip" onClick={skipCurrent} disabled={savingItem || speakingLocked || isRecording || dialogueInProgress}>Skip</button>
                );
              })()}
              <button className="ep-btn-prev" onClick={goPrev} disabled={(activeSection === 0 && currentItem === 0) || savingItem || isRecording || dialogueInProgress}>Previous</button>
              {isLastItemOverall && lastItemDone ? (
                /* Speaking (last section) already saved/evaluated -> Submit Exam Prep */
                <button className="ep-btn-save-next submit" onClick={() => setShowSubmitPopup(true)} disabled={savingItem}>Submit Exam Prep</button>
              ) : isLastItemOverall ? (
                /* On the last item of the exam (speaking), evaluate then reveal Submit. */
                <button className="ep-btn-save-next" onClick={async () => { setSavingItem(true); await saveCurrentItem(); markCurrentSaved(); setSavingItem(false); setLastItemDone(true); }} disabled={savingItem || dialogueInProgress || !isCurrentAnswered}>
                  {savingItem ? 'Saving...' : 'Save and Next'}
                </button>
              ) : (
                /* Last item of a non-final section reads "Next section - <name>"; otherwise "Save and Next" */
                <button className="ep-btn-save-next" onClick={saveAndNext} disabled={savingItem || dialogueInProgress || !isCurrentAnswered}>
                  {savingItem ? 'Saving...' : (showNextSectionLabel ? `Save and Next - ${nextSectionName}` : 'Save and Next')}
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
      <Footer />

      {/* Submitting overlay */}
      {submitting && (
        <div className="bw-confirm-overlay">
          <div className="ep-gen-loading">
            <img src="/images/loadingicon.svg" alt="" className="ep-submitting-icon" />
            <h3 className="ep-gen-loading-title">Almost there!</h3>
            <p className="ep-gen-loading-text">We&#8217;re checking your answers and putting together your results. This may take a moment.</p>
          </div>
        </div>
      )}

      {/* Exit popup */}
      {showExitPopup && (
        <div className="bw-confirm-overlay">
          <div className="bw-confirm-modal" style={{ padding: '0', width: '580px', minWidth: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ borderTop: '3px solid #7500C0', padding: '16px 32px 0' }}>
              <h3 style={{ fontFamily: 'Graphik', fontSize: '20px', fontWeight: '400', margin: '0 0 16px' }}>Exit Exam Prep?</h3>
            </div>
            <div style={{ padding: '0 32px' }}>
              <div style={{ background: '#FAFAFA', border: '1px solid #CFCFCF', padding: '12px 16px', display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <img src="/images/warningicon.svg" alt="" width="24" height="24" />
                <span style={{ fontFamily: 'Graphik', fontSize: '14px' }}>The progress will be lost and you will need to start over from the beginning. This cannot be undone.</span>
              </div>
              <p style={{ fontFamily: 'Graphik-Semibold', fontSize: '14px', color: '#555', margin: '0 0 12px' }}>What you will lose</p>
              <div style={{ border: '1px solid #CFCFCF' }}>
                {sections.map((s, i) => {
                  const secItems = getSectionItems(sections[i]);
                  const allAnswered = secItems.length > 0 && secItems.every(it => {
                    const k = it.question?.q_id || it.task?.task_id;
                    return (answers[k] && answers[k] !== '__skipped__') || (it.task && recordings[it.task.task_id]);
                  });
                  // Completed when all items answered (including the active section
                  // once its last item is done); in-progress only for the active,
                  // not-yet-completed section; future sections not started.
                  const isCompleted = allAnswered && (i < activeSection || i === activeSection);
                  const isInProgress = i === activeSection && !allAnswered;
                  const icon = isCompleted
                    ? '/images/Done.svg'
                    : isInProgress ? '/images/inprogressicon.svg'
                    : i < activeSection ? '/images/doneicon.svg' : '/images/yettostarticon.svg';
                  const label = isCompleted ? 'Completed' : isInProgress ? 'Currently in progress' : i < activeSection ? 'Completed' : 'Not Started yet';
                  const bg = isInProgress ? '#E6DCFF' : 'transparent';
                  const border = isInProgress ? '#7500C0' : '#CFCFCF';
                  return (
                    <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: i < sections.length - 1 ? '1px solid #CFCFCF' : 'none' }}>
                      <span style={{ fontFamily: 'Graphik-Semibold', fontSize: '16px' }}>{SECTION_LABELS[s.key]}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: 'Graphik', fontSize: '13px', padding: '6px 14px', borderRadius: '20px', border: '1px solid ' + border, background: bg, color: '#000' }}>
                        <img src={icon} alt="" width="16" height="16" />{label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', background: '#F1F1EF', padding: '12px 32px', marginTop: '24px' }}>
              <button style={{ padding: '10px 24px', background: 'transparent', border: '1px solid #000', fontFamily: 'Graphik-Semibold', fontSize: '14px', cursor: 'pointer' }} onClick={confirmExit}>Yes, exit and lose progress</button>
              <button style={{ padding: '10px 24px', background: '#000', color: '#fff', border: '1px solid #000', fontFamily: 'Graphik-Semibold', fontSize: '14px', cursor: 'pointer' }} onClick={() => setShowExitPopup(false)}>Continue Exam Prep</button>
            </div>
          </div>
        </div>
      )}

      {/* Time's up popup — responses auto-submitted; click to view the result. */}
      {showTimeoutPopup && (
        <div className="bw-confirm-overlay">
          <div className="bw-confirm-modal" style={{ padding: '0', width: '520px', minWidth: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ borderTop: '3px solid #7500C0', padding: '16px 32px 0' }}>
              <h3 style={{ fontFamily: 'Graphik-Semibold', fontSize: '20px', margin: '0 0 12px' }}>Time&#39;s Up!</h3>
            </div>
            <div style={{ padding: '0 32px 8px' }}>
              <div style={{ background: '#FAF5FF', border: '1px solid #E6DCFF', borderLeft: '3px solid #7500C0', padding: '12px 16px', display: 'flex', gap: '16px', marginBottom: '12px' }}>
                <img src="/images/purpleInfo.svg" alt="" width="20" height="20" onError={e => { e.target.style.display = 'none'; }} />
                <span style={{ fontFamily: 'Graphik', fontSize: '14px', lineHeight: 1.5 }}>
                  Time&#39;s up! Your answered questions will be submitted automatically.
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', background: '#F1F1EF', padding: '12px 32px' }}>
              <button style={{ padding: '10px 24px', background: '#000', color: '#fff', border: '1px solid #000', fontFamily: 'Graphik-Semibold', fontSize: '14px', cursor: 'pointer' }}
                disabled={submitting} onClick={confirmTimeout}>{submitting ? 'Generating...' : 'View Result'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Submit popup */}
      {showSubmitPopup && (
        (() => {
          const stats = computeExamStats();
          return (
            <div className="bw-confirm-overlay">
              <div className="bw-confirm-modal ep-submit-modal" onClick={e => e.stopPropagation()}>
                <div className="ep-submit-head">Ready to Submit?</div>
                <div className="ep-submit-body">
                  <p className="ep-submit-lead">You're about to submit your Exam Prep.</p>
                  <div className="ep-submit-stats">
                    <span className="ep-submit-stat total"><strong>{stats.total}</strong> Total</span>
                    <span className="ep-submit-stat answer"><strong>{stats.answered}</strong> Answer</span>
                    <span className="ep-submit-stat skipped"><strong>{stats.skipped}</strong> Skipped</span>
                    <span className="ep-submit-stat attempt"><strong>{stats.attemptedPct}%</strong> Attempted</span>
                  </div>
                  {stats.skipped > 0 && (
                    <p className="ep-submit-warn"><img src="/images/yellowwarning.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} /> <strong>{stats.skipped} Skipped.</strong> Recheck all sections once before submitting your answer.</p>
                  )}
                  <p className="ep-submit-note">Any skipped questions will be submitted as not attempted, and you won't be able to make changes afterward.</p>
                </div>
                <div className="ep-submit-actions">
                  <button className="ep-submit-back" onClick={() => setShowSubmitPopup(false)}>Go Back and re-check</button>
                  <button className="ep-submit-confirm" onClick={handleSubmit}>Submit Exam Prep</button>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* Leave confirmation */}
      {showLeaveConfirm && (
        <div className="bw-confirm-overlay" onClick={cancelLeave}>
          <div className="bw-confirm-modal" onClick={e => e.stopPropagation()}>
            <p className="bw-confirm-text">Are you sure you want to leave? Your Exam-prep progress will be lost.</p>
            <div className="bw-confirm-actions">
              <button className="bw-confirm-cancel" onClick={cancelLeave}>Cancel</button>
              <button className="bw-confirm-yes" onClick={confirmLeave}>Yes, Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamPrep;
